import React, { useEffect, useRef, useState } from 'react';
import apiService from '../services/apiService';
import { tenantService, venueService } from '../services/api';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import HistoryIcon from '@mui/icons-material/History';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

/**
 * Generic searchable combobox — same UX as a <select> but with a filter input.
 * Props:
 *   options: Array<{ value: string, label: string }>
 *   value: string
 *   onChange: (value: string) => void
 *   placeholder: string
 *   disabled: boolean
 */
const SearchableSelect = ({ options = [], value, onChange, placeholder = '— Seleziona —', disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => o.value === value);

  const filtered = query.trim()
    ? options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(query.toLowerCase()))
      )
    : options;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {/* Trigger */}
      <div
        onClick={handleOpen}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px', cursor: disabled ? 'not-allowed' : 'pointer',
          border: '1px solid #d1d5db', borderRadius: 6, background: disabled ? '#f9fafb' : '#fff',
          fontSize: 14, minHeight: 36, opacity: disabled ? 0.6 : 1,
          boxShadow: open ? '0 0 0 2px #6366f1' : undefined,
        }}
      >
        <span style={{ color: selected ? '#111' : '#9ca3af', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ color: '#9ca3af', fontSize: 11, marginLeft: 6 }}>{open ? '▲' : '▼'}</span>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', zIndex: 1000, top: '100%', left: 0, right: 0, marginTop: 2,
          background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 260, display: 'flex', flexDirection: 'column',
        }}>
          {/* Search input */}
          <div style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Cerca..."
              style={{
                width: '100%', boxSizing: 'border-box',
                border: '1px solid #e5e7eb', borderRadius: 4,
                padding: '4px 8px', fontSize: 13, outline: 'none',
              }}
            />
          </div>
          {/* Options */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <div
              onClick={() => handleSelect('')}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer', color: '#9ca3af',
                background: !value ? '#f3f4f6' : undefined,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
              onMouseLeave={e => e.currentTarget.style.background = !value ? '#f3f4f6' : ''}
            >
              {placeholder}
            </div>
            {filtered.length === 0 && (
              <div style={{ padding: '7px 12px', fontSize: 13, color: '#9ca3af' }}>Nessun risultato</div>
            )}
            {filtered.map(o => (
              <div
                key={o.value}
                onClick={() => handleSelect(o.value)}
                style={{
                  padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                  background: o.value === value ? '#ede9fe' : undefined,
                  fontWeight: o.value === value ? 600 : undefined,
                }}
                onMouseEnter={e => e.currentTarget.style.background = o.value === value ? '#ede9fe' : '#f3f4f6'}
                onMouseLeave={e => e.currentTarget.style.background = o.value === value ? '#ede9fe' : ''}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span>{o.label}</span>
                  {o.sublabel && (
                    <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>{o.sublabel}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, tone = 'slate' }) => {
  const tones = {
    slate: { background: '#f8fafc', border: '#e2e8f0', label: '#64748b', value: '#0f172a' },
    blue: { background: '#eff6ff', border: '#bfdbfe', label: '#2563eb', value: '#1d4ed8' },
    emerald: { background: '#ecfdf5', border: '#a7f3d0', label: '#059669', value: '#047857' },
    amber: { background: '#fffbeb', border: '#fde68a', label: '#b45309', value: '#92400e' },
  };
  const currentTone = tones[tone] || tones.slate;

  return (
    <div style={{
      minWidth: 150,
      flex: '1 1 150px',
      background: currentTone.background,
      border: `1px solid ${currentTone.border}`,
      borderRadius: 10,
      padding: '10px 12px',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: currentTone.label, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: currentTone.value, wordBreak: 'break-word' }}>
        {value}
      </div>
    </div>
  );
};

const FormSection = ({ eyebrow, title, subtitle, children, background = '#ffffff', border = '#e5e7eb' }) => (
  <div style={{
    border: `1px solid ${border}`,
    borderRadius: 14,
    background,
    overflow: 'visible',
  }}>
    <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${border}` }}>
      {eyebrow && (
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748b', marginBottom: 4 }}>
          {eyebrow}
        </div>
      )}
      <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>{subtitle}</div>}
    </div>
    <div style={{ padding: '16px' }}>
      {children}
    </div>
  </div>
);

const DeviceBadgeList = ({ devices = [], tone = 'blue', emptyLabel = 'Nessun device' }) => {
  const tones = {
    blue: { background: '#e0f2fe', color: '#0369a1', border: '#bae6fd' },
    green: { background: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
    amber: { background: '#fffbeb', color: '#b45309', border: '#fde68a' },
    rose: { background: '#fff1f2', color: '#be123c', border: '#fecdd3' },
    slate: { background: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  };
  const currentTone = tones[tone] || tones.blue;

  if (!devices.length) {
    return <div style={{ fontSize: 12, color: '#94a3b8' }}>{emptyLabel}</div>;
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {devices.slice(0, 10).map((device) => (
          <span
            key={device.id}
            style={{
              background: currentTone.background,
              color: currentTone.color,
              border: `1px solid ${currentTone.border}`,
              borderRadius: 999,
              padding: '3px 8px',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {device.label}
          </span>
        ))}
      </div>
      {devices.length > 10 && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
          Mostrati i primi 10 device. Altri {devices.length - 10} device non sono mostrati nella lista.
        </div>
      )}
    </>
  );
};

const SCOPES = ['GLOBAL', 'TENANT', 'VENUE_CATEGORY', 'VENUE', 'DEVICE'];
const STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];

const statusColor = {
  DRAFT: '#888',
  PUBLISHED: '#16a34a',
  ARCHIVED: '#dc2626',
};

const STATUS_SORT_ORDER = {
  PUBLISHED: 0,
  DRAFT: 1,
  ARCHIVED: 2,
};

const REVIEW_QR_TIMEOUT_DEFAULT_SECONDS = 15;
const REVIEW_QR_TIMEOUT_MIN_SECONDS = 3;
const REVIEW_QR_TIMEOUT_MAX_SECONDS = 60;
const TIP_TEXT_DEFAULT_TITLE = {
  it: 'Tocca per lasciare una mancia',
  en: 'Tap to Tip',
};
const TIP_TEXT_DEFAULT_SUBTITLE = {
  it: 'Un piccolo gesto merita un grande grazie.',
  en: 'A small gesture deserves a big thank you.',
};
const REVIEW_QR_DEFAULT_HINT_TEXT = {
  it: 'Google Recensioni',
  en: 'Google Reviews',
};
const REVIEW_QR_DEFAULT_SUBTITLE_TEXT = {
  it: 'Scansiona il QR code per lasciare una recensione',
  en: 'Scan the QR code to leave a review',
};
const REVIEW_QR_HINT_TEXT_MAX_LENGTH = 40;

const LOCALIZED_LANGUAGES = ['it', 'en'];

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

const readLocalizedText = (value, lang) => {
  if (!isPlainObject(value)) return '';
  const raw = value[lang];
  return typeof raw === 'string' ? raw : '';
};

const validateLocalizedField = (payload, fieldName, options = {}) => {
  const { maxLength } = options;
  const value = payload[fieldName];

  if (value === undefined) {
    return `Payload non valido: "${fieldName}" è obbligatorio.`;
  }

  if (!isPlainObject(value)) {
    return `Payload non valido: "${fieldName}" deve essere un oggetto con campi "it" e "en".`;
  }

  for (const lang of LOCALIZED_LANGUAGES) {
    const rawValue = value[lang];
    if (rawValue === undefined) {
      return `Payload non valido: "${fieldName}.${lang}" deve essere presente.`;
    }
    if (typeof rawValue !== 'string') {
      return `Payload non valido: "${fieldName}.${lang}" deve essere una stringa.`;
    }
    if (!rawValue.trim()) {
      return `Payload non valido: "${fieldName}.${lang}" è obbligatorio.`;
    }
    if (typeof maxLength === 'number' && rawValue.trim().length > maxLength) {
      return `Payload non valido: "${fieldName}.${lang}" deve avere massimo ${maxLength} caratteri.`;
    }
  }

  return null;
};

const validateOptionalNumberRange = (value, fieldName, min, max) => {
  if (value === undefined) return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < min || numericValue > max) {
    return `Payload non valido: "${fieldName}" deve essere un numero tra ${min} e ${max}.`;
  }
  return null;
};

const isValidAbsoluteReviewUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false;

  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const validateNamespacePayload = (namespace, payload) => {
  if (namespace !== 'review_qr' && namespace !== 'tip_text') return { ok: true };

  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { ok: false, error: `Payload ${namespace} non valido: deve essere un oggetto JSON.` };
  }

  if (namespace === 'tip_text') {
    const titleError = validateLocalizedField(payload, 'title');
    if (titleError) return { ok: false, error: titleError };

    const subtitleError = validateLocalizedField(payload, 'subtitle');
    if (subtitleError) return { ok: false, error: subtitleError };

    const small = payload.small;
    if (small !== undefined && !isPlainObject(small)) {
      return { ok: false, error: 'Payload non valido: "small" deve essere un oggetto.' };
    }

    const mediumLarge = payload.mediumLarge;
    if (mediumLarge !== undefined && !isPlainObject(mediumLarge)) {
      return { ok: false, error: 'Payload non valido: "mediumLarge" deve essere un oggetto.' };
    }

    const smallTitleError = validateOptionalNumberRange(small?.titleFontSize, 'small.titleFontSize', 12, 96);
    if (smallTitleError) return { ok: false, error: smallTitleError };
    const smallSubtitleError = validateOptionalNumberRange(small?.subtitleFontSize, 'small.subtitleFontSize', 10, 72);
    if (smallSubtitleError) return { ok: false, error: smallSubtitleError };
    const mediumTitleError = validateOptionalNumberRange(mediumLarge?.titleFontSize, 'mediumLarge.titleFontSize', 12, 96);
    if (mediumTitleError) return { ok: false, error: mediumTitleError };
    const mediumSubtitleError = validateOptionalNumberRange(mediumLarge?.subtitleFontSize, 'mediumLarge.subtitleFontSize', 10, 72);
    if (mediumSubtitleError) return { ok: false, error: mediumSubtitleError };

    return { ok: true };
  }

  if (payload.timeoutSeconds !== undefined) {
    const timeout = Number(payload.timeoutSeconds);
    if (!Number.isFinite(timeout) || timeout < REVIEW_QR_TIMEOUT_MIN_SECONDS || timeout > REVIEW_QR_TIMEOUT_MAX_SECONDS) {
      return {
        ok: false,
        error: `Payload review_qr non valido: "timeoutSeconds" deve essere tra ${REVIEW_QR_TIMEOUT_MIN_SECONDS} e ${REVIEW_QR_TIMEOUT_MAX_SECONDS}.`,
      };
    }
  }

  if (payload.reviewUrl !== undefined && !isValidAbsoluteReviewUrl(payload.reviewUrl)) {
    return { ok: false, error: 'Payload review_qr non valido: "reviewUrl" deve essere una URL assoluta HTTP/HTTPS.' };
  }

  if (payload.showHintText !== undefined && typeof payload.showHintText !== 'boolean') {
    return { ok: false, error: 'Payload review_qr non valido: "showHintText" deve essere true/false.' };
  }

  const subtitleTextError = validateLocalizedField(payload, 'subtitleText');
  if (subtitleTextError) return { ok: false, error: subtitleTextError };

  const hintTextError = validateLocalizedField(payload, 'hintText', { maxLength: REVIEW_QR_HINT_TEXT_MAX_LENGTH });
  if (hintTextError) return { ok: false, error: hintTextError };

  if (!payload.reviewUrl || !String(payload.reviewUrl).trim()) {
    return { ok: false, error: 'Payload review_qr non valido: "reviewUrl" è obbligatoria.' };
  }

  return { ok: true };
};

const NAMESPACE_CATALOG = [
  {
    id: 'carousel',
    label: 'carousel — Carousel Immagini',
    description: 'Immagini mostrate in background durante la selezione mancia. Scope: GLOBAL, TENANT, VENUE.',
    defaultPayload: { items: [] },
  },
  {
    id: 'tip_amount',
    label: 'tip_amount — Importi Mancia',
    description: 'Configura i 3 importi predefiniti sui pulsanti di selezione mancia. Il pulsante "Importo libero" è sempre presente.',
    defaultPayload: { items: [{ amountCents: 200 }, { amountCents: 500 }, { amountCents: 1000 }] },
  },
  {
    id: 'tip_text',
    label: 'tip_text — Testi Schermata Mancia',
    description: 'Configura il titolo, il sottotitolo bilingue (it/en) e opzionalmente la dimensione del testo nella schermata di selezione mancia. Il totem userà la variante coerente con la lingua cliente selezionata.',
    defaultPayload: {
      title: TIP_TEXT_DEFAULT_TITLE,
      subtitle: TIP_TEXT_DEFAULT_SUBTITLE,
      small: {
        titleFontSize: 48,
        subtitleFontSize: 24,
      },
      mediumLarge: {
        titleFontSize: 56,
        subtitleFontSize: 21,
      },
    },
  },
  {
    id: 'review_qr',
    label: 'review_qr — QR Recensioni',
    description: 'Controlla visualizzazione QR nella schermata di pagamento riuscito con timeout di rientro, URL recensione e testi bilingue (it/en). Il totem userà la variante coerente con la lingua cliente selezionata.',
    defaultPayload: {
      timeoutSeconds: REVIEW_QR_TIMEOUT_DEFAULT_SECONDS,
      reviewUrl: '',
      showHintText: false,
      hintText: REVIEW_QR_DEFAULT_HINT_TEXT,
      subtitleText: REVIEW_QR_DEFAULT_SUBTITLE_TEXT,
    },
  },
];
const SMART_NAMESPACES = ['carousel', 'tip_amount', 'tip_text', 'review_qr'];

const ConfigPayloadDetails = ({ profile, mediaAssets }) => {
  if (profile.namespace === 'carousel') {
    const items = Array.isArray(profile.payload?.items) ? profile.payload.items : [];
    return (
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
          Immagini carousel ({items.length})
        </div>
        {items.length === 0 && <p style={{ fontSize: 13, color: '#aaa' }}>Nessun media configurato.</p>}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {items.map((item, idx) => {
            const linkedAsset = item.assetId ? mediaAssets.find(a => a.id === item.assetId) : null;
            const mimeType = item.mimeType || linkedAsset?.mimeType || '';
            const isVideo = mimeType.startsWith('video/');
            return (
              <div key={idx} style={{ width: 140, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden', background: '#fff', fontSize: 12 }}>
                {item.assetId ? (
                  isVideo ? (
                    <div style={{ width: '100%', height: 90, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 28 }}>
                      🎬
                    </div>
                  ) : (
                    <img
                      src={apiService.getMediaPreviewUrl(item.assetId)}
                      alt={item.title || `Slide ${idx + 1}`}
                      style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
                    />
                  )
                ) : (
                  <div style={{ width: '100%', height: 90, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 11 }}>
                    Nessun media
                  </div>
                )}
                <div style={{ padding: '4px 6px', color: '#555' }}>
                  <div style={{ fontWeight: 500 }}>#{idx + 1}{item.title ? ` — ${item.title}` : ''}</div>
                  {mimeType && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{isVideo ? 'Video' : 'Immagine'}</div>}
                  {item.assetId && <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#bbb', marginTop: 2 }}>{item.assetId.slice(0, 10)}…</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (profile.namespace === 'tip_amount') {
    const items = Array.isArray(profile.payload?.items) ? profile.payload.items : [];
    return (
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>💶 Importi configurati</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {items.map((item, idx) => (
            <div key={idx} style={{
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
              padding: '12px 20px', fontSize: 24, fontWeight: 700, color: '#111',
              minWidth: 80, textAlign: 'center',
            }}>
              €{item.amountCents % 100 === 0 ? (item.amountCents / 100) : (item.amountCents / 100).toFixed(2)}
            </div>
          ))}
          <div style={{
            background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8,
            padding: '12px 20px', fontSize: 13, color: '#9ca3af', fontStyle: 'italic',
          }}>
            + Importo libero
          </div>
        </div>
      </div>
    );
  }

  if (profile.namespace === 'tip_text') {
    const titleIt = readLocalizedText(profile.payload?.title, 'it') || TIP_TEXT_DEFAULT_TITLE.it;
    const titleEn = readLocalizedText(profile.payload?.title, 'en') || TIP_TEXT_DEFAULT_TITLE.en;
    const subtitleIt = readLocalizedText(profile.payload?.subtitle, 'it') || TIP_TEXT_DEFAULT_SUBTITLE.it;
    const subtitleEn = readLocalizedText(profile.payload?.subtitle, 'en') || TIP_TEXT_DEFAULT_SUBTITLE.en;
    const smallTitleFontSize = typeof profile.payload?.small?.titleFontSize === 'number'
      ? profile.payload.small.titleFontSize
      : typeof profile.payload?.titleFontSize === 'number'
        ? profile.payload.titleFontSize
        : null;
    const smallSubtitleFontSize = typeof profile.payload?.small?.subtitleFontSize === 'number'
      ? profile.payload.small.subtitleFontSize
      : typeof profile.payload?.subtitleFontSize === 'number'
        ? profile.payload.subtitleFontSize
        : null;
    const mediumLargeTitleFontSize = typeof profile.payload?.mediumLarge?.titleFontSize === 'number'
      ? profile.payload.mediumLarge.titleFontSize
      : typeof profile.payload?.titleFontSize === 'number'
        ? profile.payload.titleFontSize
        : null;
    const mediumLargeSubtitleFontSize = typeof profile.payload?.mediumLarge?.subtitleFontSize === 'number'
      ? profile.payload.mediumLarge.subtitleFontSize
      : typeof profile.payload?.subtitleFontSize === 'number'
        ? profile.payload.subtitleFontSize
        : null;
    return (
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>📝 Testi configurati</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Titolo</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>IT</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 8 }}>{titleIt}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>EN</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>{titleEn}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Sottotitolo</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>IT</div>
            <div style={{ fontSize: 15, color: '#374151', marginBottom: 8 }}>{subtitleIt}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>EN</div>
            <div style={{ fontSize: 15, color: '#374151' }}>{subtitleEn}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>Dimensioni small tablet</div>
            <div style={{ fontSize: 12, color: '#374151' }}>Titolo: {smallTitleFontSize ?? 'default app'}</div>
            <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>Sottotitolo: {smallSubtitleFontSize ?? 'default app'}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>Dimensioni medium / large tablet</div>
            <div style={{ fontSize: 12, color: '#374151' }}>Titolo: {mediumLargeTitleFontSize ?? 'default app'}</div>
            <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>Sottotitolo: {mediumLargeSubtitleFontSize ?? 'default app'}</div>
          </div>
        </div>
      </div>
    );
  }

  if (profile.namespace === 'review_qr') {
    const timeoutSeconds = Number.isFinite(profile.payload?.timeoutSeconds)
      ? profile.payload.timeoutSeconds
      : REVIEW_QR_TIMEOUT_DEFAULT_SECONDS;
    const reviewUrl = typeof profile.payload?.reviewUrl === 'string' ? profile.payload.reviewUrl : '';
    const showHintText = typeof profile.payload?.showHintText === 'boolean' ? profile.payload.showHintText : false;
    const hintTextIt = readLocalizedText(profile.payload?.hintText, 'it') || REVIEW_QR_DEFAULT_HINT_TEXT.it;
    const hintTextEn = readLocalizedText(profile.payload?.hintText, 'en') || REVIEW_QR_DEFAULT_HINT_TEXT.en;
    const subtitleTextIt = readLocalizedText(profile.payload?.subtitleText, 'it') || REVIEW_QR_DEFAULT_SUBTITLE_TEXT.it;
    const subtitleTextEn = readLocalizedText(profile.payload?.subtitleText, 'en') || REVIEW_QR_DEFAULT_SUBTITLE_TEXT.en;

    return (
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>⭐ QR recensioni</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
            Stato: <strong>QR code attivato</strong>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
            Timeout ritorno home: <strong>{timeoutSeconds}s</strong>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
            URL recensione: {reviewUrl ? <span style={{ color: '#1d4ed8' }}>{reviewUrl}</span> : <span style={{ color: '#9ca3af' }}>non impostata</span>}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
            Testo sopra al QR (sottotitolo presente sotto la scritta Thank you):
            <div style={{ marginTop: 6, color: '#374151' }}>IT: {subtitleTextIt}</div>
            <div style={{ marginTop: 4, color: '#374151' }}>EN: {subtitleTextEn}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
            Testo sotto al QR: <strong>{showHintText ? 'Mostrato' : 'Nascosto'}</strong>
            {showHintText && (
              <>
                <div style={{ marginTop: 6, color: '#374151' }}>IT: {hintTextIt}</div>
                <div style={{ marginTop: 4, color: '#374151' }}>EN: {hintTextEn}</div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Payload:</div>
      <pre style={{ background: '#f6f8fa', border: '1px solid #e1e4e8', borderRadius: 6, padding: '12px 14px', fontSize: 12, fontFamily: 'monospace', overflowX: 'auto', marginTop: 0, maxHeight: 400, overflowY: 'auto' }}>{JSON.stringify(profile.payload, null, 2)}</pre>
    </>
  );
};

const RemoteDeviceConfig = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterScope, setFilterScope] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterNamespace, setFilterNamespace] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('default');
  const [sortDirection, setSortDirection] = useState('desc');

  // Create / edit form
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ scope: 'GLOBAL', scopeEntityId: '', namespace: '', payloadText: '{}' });
  const [originalForm, setOriginalForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payloadError, setPayloadError] = useState(null);

  // Version history panel
  const [versionsProfileId, setVersionsProfileId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);

  // Read-only detail view
  const [viewingProfile, setViewingProfile] = useState(null);

  // Smart payload editor
  const [mediaAssets, setMediaAssets] = useState([]);
  const [mediaFolders, setMediaFolders] = useState([]);  // folder list for carousel filter
  const [carouselFolderFilter, setCarouselFolderFilter] = useState(''); // '' = tutte
  const [mediaAssetsLoading, setMediaAssetsLoading] = useState(false);

  const [showRawPayload, setShowRawPayload] = useState(false);

  // Publish/archive/rollback confirmation modal
  const [pubModal, setPubModal] = useState(null); // { profileId, namespace, scope, mode: 'publish'|'save'|'archive'|'rollback', versionId? } | null
  const [pubModalDevices, setPubModalDevices] = useState(null); // { count, devices } | null
  const [pubModalLoading, setPubModalLoading] = useState(false);
  const [pubWsNotify, setPubWsNotify] = useState(true);
  const [errorModal, setErrorModal] = useState(null); // { title, message, icon?, accentColor?, bgColor?, borderColor? } | null

  // Scope entity dropdowns
  const [tenantsForSelect, setTenantsForSelect] = useState([]);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [scopeOptionsLoading, setScopeOptionsLoading] = useState(false);

  const SCOPE_ENTITY_LABEL = {
    TENANT: 'Tenant target',
    VENUE_CATEGORY: 'Venue category target',
    VENUE: 'Venue target',
    DEVICE: 'Device target',
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filterScope) params.scope = filterScope;
      if (filterStatus) params.status = filterStatus;
      if (filterNamespace) params.namespace = filterNamespace;
      const res = await apiService.getRemoteConfigs(params);
      setProfiles(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterScope, filterStatus, filterNamespace]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortDirection(field === 'publishedAt' || field === 'version' ? 'desc' : 'asc');
  };

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const visibleProfiles = profiles
    .filter((profile) => {
      if (!normalizedSearchQuery) return true;

      const searchableParts = [
        profile.namespace,
        profile.scope,
        profile.scopeEntityLabel,
        profile.scopeEntityId,
        profile.status,
        profile.configVersion,
        profile.id,
      ]
        .filter(Boolean)
        .map(value => String(value).toLowerCase());

      return searchableParts.some(value => value.includes(normalizedSearchQuery));
    })
    .slice()
    .sort((left, right) => {
      if (sortField === 'default') {
        const leftStatusRank = STATUS_SORT_ORDER[left.status] ?? 99;
        const rightStatusRank = STATUS_SORT_ORDER[right.status] ?? 99;
        if (leftStatusRank !== rightStatusRank) return leftStatusRank - rightStatusRank;

        const leftDate = left.publishedAt ? new Date(left.publishedAt).getTime() : 0;
        const rightDate = right.publishedAt ? new Date(right.publishedAt).getTime() : 0;
        if (leftDate !== rightDate) return rightDate - leftDate;

        const leftUpdatedAt = left.updatedAt ? new Date(left.updatedAt).getTime() : 0;
        const rightUpdatedAt = right.updatedAt ? new Date(right.updatedAt).getTime() : 0;
        return rightUpdatedAt - leftUpdatedAt;
      }

      const direction = sortDirection === 'asc' ? 1 : -1;
      const readValue = (profile) => {
        switch (sortField) {
          case 'namespace':
            return profile.namespace || '';
          case 'scope':
            return profile.scope || '';
          case 'scopeEntity':
            return profile.scopeEntityLabel || profile.scopeEntityId || '';
          case 'status':
            return STATUS_SORT_ORDER[profile.status] ?? 99;
          case 'version':
            return profile.version || 0;
          case 'configVersion':
            return profile.configVersion || '';
          case 'publishedAt':
            return profile.publishedAt ? new Date(profile.publishedAt).getTime() : 0;
          default:
            return '';
        }
      };

      const leftValue = readValue(left);
      const rightValue = readValue(right);

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        if (leftValue === rightValue) return 0;
        return leftValue > rightValue ? direction : -direction;
      }

      return String(leftValue).localeCompare(String(rightValue), 'it', { sensitivity: 'base' }) * direction;
    });

  const sortIndicator = (field) => {
    if (sortField === 'default' && field === 'publishedAt') return ' ↓';
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Load media assets + folders when smart editor is needed
  useEffect(() => {
    if (!formOpen || !SMART_NAMESPACES.includes(form.namespace)) return;
    setMediaAssetsLoading(true);
    Promise.all([
      apiService.getMediaAssets(),
      apiService.getMediaFolders(),
    ])
      .then(([assetsRes, foldersRes]) => {
        setMediaAssets(assetsRes.data || []);
        setMediaFolders(Array.isArray(foldersRes.data) ? foldersRes.data : []);
      })
      .catch(() => { setMediaAssets([]); setMediaFolders([]); })
      .finally(() => setMediaAssetsLoading(false));
  }, [formOpen, form.namespace]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load tenants when form opens (used for TENANT scope dropdown)
  useEffect(() => {
    if (!formOpen) return;
    tenantService.getTenants()
      .then(data => setTenantsForSelect(Array.isArray(data) ? data : []))
      .catch(() => setTenantsForSelect([]));
  }, [formOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load scope-specific options when scope changes
  useEffect(() => {
    if (!formOpen || form.scope === 'GLOBAL' || form.scope === 'TENANT') {
      setScopeOptions([]);
      return;
    }
    setScopeOptionsLoading(true);
    const loaders = {
      VENUE_CATEGORY: () => apiService.getVenueCategories().then(res => (res.data || []).map(c => ({ value: c.id, label: c.name }))),
      VENUE: () => venueService.getVenues().then(res => {
        const list = res?.venues || res?.data || (Array.isArray(res) ? res : []);
        return list.map(v => ({ value: v.id, label: v.name }));
      }),
      DEVICE: () => apiService.getAllDevices().then(list => (Array.isArray(list) ? list : []).map(d => ({
        value: d.id,
        label: d.label || d.id,
        sublabel: d.setupCode || undefined,
      }))),
    };
    (loaders[form.scope] || (() => Promise.resolve([])))()
      .then(setScopeOptions)
      .catch(() => setScopeOptions([]))
      .finally(() => setScopeOptionsLoading(false));
  }, [form.scope, formOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null);
    setOriginalForm(null);
    setForm({ scope: 'GLOBAL', scopeEntityId: '', namespace: '', payloadText: '{}' });
    setPayloadError(null);
    setShowRawPayload(false);
    setFormOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await apiService.getRemoteConfig(id);
      const profile = res.data;
      const f = {
        scope: profile.scope,
        scopeEntityId: profile.scopeEntityId || '',
        namespace: profile.namespace,
        payloadText: JSON.stringify(profile.payload, null, 2),
      };
      setEditingId(id);
      setForm(f);
      setOriginalForm(f);
      setPayloadError(null);
      setShowRawPayload(false);
      setFormOpen(true);
    } catch (err) {
      showErrorModal('Impossibile aprire la configurazione', getErrorMessage(err));
    }
  };

  const validatePayload = (text) => {
    try {
      return { ok: true, value: JSON.parse(text) };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };

  const getErrorMessage = (err) => err?.response?.data?.error || err?.message || 'Si è verificato un errore inatteso.';

  const showErrorModal = (title, message, options = {}) => {
    setErrorModal({
      title,
      message,
      icon: options.icon || '⚠️',
      accentColor: options.accentColor || '#9f1239',
      bgColor: options.bgColor || '#fff1f2',
      borderColor: options.borderColor || '#fecdd3',
    });
  };

  const findLocalActiveConflict = ({ scope, scopeEntityId, namespace, excludeId = null }) => {
    return profiles.find((profile) => (
      profile.status !== 'ARCHIVED'
      && profile.scope === scope
      && (profile.scopeEntityId || '') === (scopeEntityId || '')
      && profile.namespace === namespace
      && profile.id !== excludeId
    )) || null;
  };

  const getConflictMessage = (conflictingProfile) => {
    const stateLabel = conflictingProfile?.status === 'PUBLISHED' ? 'pubblicata' : 'in bozza';
    return `Esiste già una configurazione ${stateLabel} per questo namespace/scope/entità (id: ${conflictingProfile?.id}). Modificala direttamente oppure archiviala prima di crearne una nuova.`;
  };

  const doSave = async (notifyWs) => {
    const { value } = validatePayload(form.payloadText); // already validated before modal
    try {
      setSaving(true);
      const data = {
        scope: form.scope,
        namespace: form.namespace,
        payload: value,
        scopeEntityId: form.scopeEntityId || null,
        notifyWs,
      };
      if (editingId) {
        await apiService.updateRemoteConfig(editingId, data);
      } else {
        await apiService.createRemoteConfig(data);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      showErrorModal('Salvataggio non riuscito', getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const { ok, error: parseError, value } = validatePayload(form.payloadText);
    if (!ok) {
      setPayloadError(`JSON non valido: ${parseError}`);
      return;
    }

    const semanticValidation = validateNamespacePayload(form.namespace, value);
    if (!semanticValidation.ok) {
      setPayloadError(semanticValidation.error);
      return;
    }

    setPayloadError(null);

    const conflictingProfile = findLocalActiveConflict({
      scope: form.scope,
      scopeEntityId: form.scopeEntityId || null,
      namespace: form.namespace,
      excludeId: editingId,
    });

    if (conflictingProfile) {
      showErrorModal('Configurazione già presente', getConflictMessage(conflictingProfile), {
        icon: '⛔',
        accentColor: '#9a3412',
        bgColor: '#fff7ed',
        borderColor: '#fdba74',
      });
      return;
    }

    const editingProfile = profiles.find(p => p.id === editingId);
    const isPublished = editingId && editingProfile?.status === 'PUBLISHED';

    if (isPublished) {
      // Open publish modal — actual save triggered by handlePubConfirm
      setPubWsNotify(true);
      setPubModalDevices(null);
      setPubModal({ profileId: editingId, namespace: form.namespace, scope: form.scope, mode: 'save' });
      setPubModalLoading(true);
      // Pass current form scope/scopeEntityId/namespace so the preview reflects unsaved published edits
      apiService.getRemoteConfigImpactedDevices(editingId, {
        scope: form.scope,
        scopeEntityId: form.scopeEntityId || null,
        namespace: form.namespace,
      })
        .then(res => setPubModalDevices(res.data))
        .catch(() => setPubModalDevices({ count: '?', devices: [] }))
        .finally(() => setPubModalLoading(false));
      return;
    }

    // DRAFT: save directly without modal
    await doSave(true);
  };

  const publish = async (id) => {
    const profile = profiles.find(p => p.id === id);
    setPubWsNotify(true);
    setPubModalDevices(null);
    setPubModal({ profileId: id, namespace: profile?.namespace || '—', scope: profile?.scope || '—', mode: 'publish' });
    setPubModalLoading(true);
    apiService.getRemoteConfigImpactedDevices(id)
      .then(res => setPubModalDevices(res.data))
      .catch(() => setPubModalDevices({ count: '?', devices: [] }))
      .finally(() => setPubModalLoading(false));
  };

  const archive = async (id) => {
    const profile = profiles.find(p => p.id === id);
    setPubWsNotify(true);
    setPubModalDevices(null);
    setPubModal({ profileId: id, namespace: profile?.namespace || '—', scope: profile?.scope || '—', mode: 'archive' });
    setPubModalLoading(true);
    apiService.getRemoteConfigImpactedDevices(id)
      .then(res => setPubModalDevices(res.data))
      .catch(() => setPubModalDevices({ count: '?', devices: [] }))
      .finally(() => setPubModalLoading(false));
  };

  const openRollbackModal = async (profileId, versionId) => {
    const profile = profiles.find(p => p.id === profileId);
    setPubWsNotify(true);
    setPubModalDevices(null);
    setPubModal({ profileId, versionId, namespace: profile?.namespace || '—', scope: profile?.scope || '—', mode: 'rollback' });
    setPubModalLoading(true);
    apiService.getRemoteConfigImpactedDevices(profileId)
      .then(res => setPubModalDevices(res.data))
      .catch(() => setPubModalDevices({ count: '?', devices: [] }))
      .finally(() => setPubModalLoading(false));
  };

  const handlePubConfirm = async () => {
    if (!pubModal) return;
    const notifyWs = pubWsNotify;
    const modal = pubModal;
    setPubModal(null);
    if (modal.mode === 'publish') {
      try {
        await apiService.publishRemoteConfig(modal.profileId, notifyWs);
        await load();
      } catch (err) {
        showErrorModal('Pubblicazione non riuscita', getErrorMessage(err));
      }
    } else if (modal.mode === 'archive') {
      try {
        await apiService.archiveRemoteConfig(modal.profileId, notifyWs);
        await load();
      } catch (err) {
        showErrorModal('Archiviazione non riuscita', getErrorMessage(err));
      }
    } else if (modal.mode === 'rollback') {
      try {
        await apiService.rollbackRemoteConfig(modal.profileId, modal.versionId, notifyWs);
        setVersionsProfileId(null);
        setViewingVersion(null);
        await load();
      } catch (err) {
        showErrorModal('Ripristino non riuscito', getErrorMessage(err));
      }
    } else if (modal.mode === 'save') {
      await doSave(notifyWs);
    }
  };

  const openVersions = async (id) => {
    setVersionsProfileId(id);
    setViewingVersion(null);
    try {
      setVersionsLoading(true);
      const res = await apiService.getRemoteConfigVersions(id);
      setVersions(res.data || []);
    } catch (err) {
      showErrorModal('Impossibile caricare le versioni', getErrorMessage(err));
    } finally {
      setVersionsLoading(false);
    }
  };

  const openView = async (id) => {
    if (viewingProfile?.id === id && viewingProfile.__viewMode !== 'version') {
      setViewingProfile(null);
      return;
    }

    try {
      const listProfile = profiles.find((profile) => profile.id === id);
      const res = await apiService.getRemoteConfig(id);
      setViewingProfile({
        ...res.data,
        scopeEntityLabel: res.data?.scopeEntityLabel || listProfile?.scopeEntityLabel || null,
        scopeEntityId: res.data?.scopeEntityId || listProfile?.scopeEntityId || null,
        __viewMode: 'profile',
      });
    } catch (err) {
      showErrorModal('Impossibile aprire il dettaglio', getErrorMessage(err));
    }
  };

  const openVersionView = (version) => {
    const parentProfile = profiles.find(p => p.id === versionsProfileId);
    setViewingVersion(viewingVersion?.id === version.id ? null : {
      ...version,
      profileId: version.profileId || versionsProfileId || null,
      namespace: parentProfile?.namespace || '—',
      scope: parentProfile?.scope || '—',
      scopeEntityId: parentProfile?.scopeEntityId || null,
      scopeEntityLabel: parentProfile?.scopeEntityLabel || null,
      __viewMode: 'version',
    });
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare questa config? L\'operazione non è reversibile.')) return;
    try {
      await apiService.deleteRemoteConfig(id);
      await load();
    } catch (err) {
      showErrorModal('Eliminazione non riuscita', getErrorMessage(err));
    }
  };

  const selectedNamespaceMeta = NAMESPACE_CATALOG.find(n => n.id === form.namespace);
  const editingProfile = profiles.find(p => p.id === editingId);
  const isEditingPublished = editingProfile?.status === 'PUBLISHED';
  const hasFormChanges = !originalForm ||
    form.scope !== originalForm.scope ||
    form.scopeEntityId !== originalForm.scopeEntityId ||
    form.namespace !== originalForm.namespace ||
    form.payloadText !== originalForm.payloadText;
  const noChanges = editingId && !hasFormChanges;
  const missingEntity = form.scope !== 'GLOBAL' && !form.scopeEntityId;
  const missingNamespace = !form.namespace;
  const isSaveDisabled = saving || noChanges || missingEntity || missingNamespace;
  const disabledTitle = noChanges ? 'Nessuna modifica apportata'
    : missingNamespace ? 'Seleziona un namespace'
    : missingEntity ? `Seleziona ${SCOPE_ENTITY_LABEL[form.scope] || 'entità scope'} prima di salvare`
    : undefined;
  const formModeTitle = editingId ? 'Modifica configurazione' : 'Crea nuova configurazione';
  const formModeSubtitle = editingId
    ? 'Aggiorna scope, namespace e payload del profilo selezionato. Se il profilo è già pubblicato, il salvataggio produrrà una nuova pubblicazione.'
    : 'Imposta un nuovo profilo remoto definendo dove si applica e quale contenuto deve ricevere il kiosk.';
  const formModeBadge = editingId
    ? (isEditingPublished ? 'Profilo pubblicato' : 'Profilo in bozza')
    : 'Nuova bozza';
  const selectedTenant = form.scope === 'TENANT'
    ? tenantsForSelect.find(t => t.id === form.scopeEntityId)
    : null;
  const selectedScopeOption = ['VENUE_CATEGORY', 'VENUE', 'DEVICE'].includes(form.scope)
    ? scopeOptions.find(option => option.value === form.scopeEntityId)
    : null;
  const effectiveTargetLabel = form.scope === 'GLOBAL'
    ? 'Tutti i device'
    : selectedTenant?.company_name
      || selectedScopeOption?.label
      || form.scopeEntityId
      || `Seleziona ${SCOPE_ENTITY_LABEL[form.scope] || 'target'}`;
  const showImpactedDiff = pubModal?.mode === 'save' && pubModalDevices?.changedImpactPreview;
  const impactedDiffMessage = !showImpactedDiff
    ? ''
    : pubModalDevices?.changedTarget && pubModalDevices?.changedNamespace
      ? 'Stai cambiando sia il target sia il namespace della configurazione pubblicata. I device che resteranno o entreranno nel target finale riceveranno il nuovo namespace; quelli che usciranno dal target dovranno ricalcolare la configurazione effettiva da usare.'
      : pubModalDevices?.changedNamespace
        ? 'Stai mantenendo lo stesso target, ma stai cambiando il namespace della configurazione pubblicata. I device coinvolti resteranno nel target, ma dovranno riallinearsi: abbandoneranno la configurazione del namespace precedente e applicheranno quella del nuovo namespace.'
        : 'Stai cambiando il target effettivo della configurazione pubblicata. Alla conferma verranno notificati sia i device che entreranno o resteranno nel target finale, sia quelli che usciranno dal target e dovranno ricalcolare quale configurazione usare.';

  return (
    <div>
      <div className="card">
        <h2>🎛️ Configurazione Remota Dispositivi</h2>
        <p>Gestisci i profili di configurazione per scope consegnati ai dispositivi kiosk. Pubblica per inviare le modifiche istantaneamente via WebSocket.</p>

        {/* ── Guida rapida ─────────────────────────────────────────── */}
        <details style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#334155', userSelect: 'none' }}>
            ℹ️ Come funziona la Configurazione Remota?
          </summary>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16, fontSize: 13, color: '#475569', lineHeight: 1.65 }}>

            {/* NAMESPACE */}
            <div>
              <strong style={{ fontSize: 13.5 }}>📦 Namespace — cosa si configura</strong>
              <p style={{ margin: '6px 0 4px' }}>
                Il <em>namespace</em> identifica il <strong>tipo di dato</strong> che si vuole configurare. Ogni namespace ha una struttura di payload dedicata e un proprio editor visuale. Si possono avere più profili attivi per namespace diversi contemporaneamente.
              </p>
              <p style={{ margin: '0 0 6px' }}>Namespace disponibili e relativa struttura:</p>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Namespace</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Cosa controlla</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Struttura payload</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Scope consigliati</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}><code>carousel</code></td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Immagini di sfondo mostrate nel kiosk durante la selezione della mancia</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 11 }}>{`{ items: [{ assetId, title, order }] }`}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>GLOBAL, TENANT, VENUE</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}><code>tip_amount</code></td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>I 3 importi predefiniti mostrati nei pulsanti di selezione mancia</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 11 }}>{`{ items: [{ amountCents }, { amountCents }, { amountCents }] }`}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>GLOBAL, TENANT, VENUE, DEVICE</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}><code>tip_text</code></td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Titolo, sottotitolo e dimensioni testo della schermata di selezione mancia</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 11 }}>{`{ title: { it, en }, subtitle: { it, en }, small: { titleFontSize, subtitleFontSize }, mediumLarge: { titleFontSize, subtitleFontSize } }`}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>GLOBAL, TENANT, VENUE, DEVICE</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}><code>review_qr</code></td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>QR recensioni post pagamento, timeout rientro, URL da convertire in QR, sottotitolo e testo descrittivo</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 11 }}>{`{ timeoutSeconds, reviewUrl, showHintText, hintText: { it, en }, subtitleText: { it, en } }`}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>GLOBAL, TENANT, VENUE, DEVICE</td>
                  </tr>
                  <tr style={{ background: '#fafafa', color: '#94a3b8' }}>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontStyle: 'italic' }}>altri namespace futuri</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontStyle: 'italic' }}>es. branding, impostazioni UI, categorie…</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>—</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>—</td>
                  </tr>
                </tbody>
              </table>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#94a3b8' }}>
                Per aggiungere un nuovo namespace: definire la struttura payload nel frontend e registrare il consumer nel kiosk-app.
              </p>
            </div>

            {/* SCOPE + MATRICE */}
            <div>
              <strong style={{ fontSize: 13.5 }}>🎯 Scope e priorità a cascata</strong>
              <p style={{ margin: '6px 0 6px' }}>
                Il kiosk, quando richiede una configurazione per un namespace, riceve <strong>sempre e solo la versione più specifica disponibile</strong>. Il sistema percorre la gerarchia dal basso verso l'alto e si ferma al primo profilo PUBLISHED trovato.
                Questo permette di definire una configurazione globale "di default" e sovrascriverla selettivamente per singoli tenant, venue o device — senza duplicare tutta la configurazione.
              </p>
              <p style={{ margin: '0 0 6px' }}><strong>Matrice di priorità</strong> (1 = massima priorità):</p>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Priorità</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Scope</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Applicato a</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Esempio d'uso</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['1 — più specifica', 'DEVICE', 'Un singolo dispositivo fisico', 'Carousel personalizzato per il kiosk del bancone VIP'],
                    ['2', 'VENUE', 'Tutti i device di un venue', 'Immagini con il logo del ristorante "Trattoria Roma"'],
                    ['3', 'VENUE_CATEGORY', 'Tutti i venue di una categoria', 'Immagini generiche per tutti i ristoranti (categoria "food")'],
                    ['4', 'TENANT', 'Tutti i device di un tenant', 'Immagini brandizzate per la catena "Burger Co."'],
                    ['5 — fallback', 'GLOBAL', 'Tutti i dispositivi senza config più specifica', 'Carousel di default con immagini SoonTip'],
                  ].map(([prio, scope, applied, example]) => (
                    <tr key={scope}>
                      <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>{prio}</td>
                      <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>{scope}</td>
                      <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>{applied}</td>
                      <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', color: '#64748b', fontStyle: 'italic' }}>{example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}>
                ⚠️ Se non esiste alcun profilo PUBLISHED per un namespace, il kiosk non riceve la configurazione e usa il comportamento di default (es. immagini statiche bundled nell'app).
              </p>
            </div>

            {/* CICLO DI VITA */}
            <div>
              <strong style={{ fontSize: 13.5 }}>🔄 Ciclo di vita di un profilo</strong>
              <table style={{ marginTop: 6, borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Stato</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Descrizione</th>
                    <th style={{ padding: '5px 8px', textAlign: 'center', border: '1px solid #e2e8f0' }}>Device la riceve?</th>
                    <th style={{ padding: '5px 8px', textAlign: 'center', border: '1px solid #e2e8f0' }}>Modificabile?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}><span style={{ color: '#2563eb', fontWeight: 600 }}>DRAFT</span></td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Bozza in lavorazione. Non è visibile ai device. Si può modificare liberamente senza impatti sulla produzione.</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>❌</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>✅ libero</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}><span style={{ color: '#16a34a', fontWeight: 600 }}>PUBLISHED</span></td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Configurazione attiva. I device la ricevono e la applicano. Ogni modifica salvata incrementa la versione e aggiorna i device in tempo reale.</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>✅</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>✅ con re-publish</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}><span style={{ color: '#dc2626', fontWeight: 600 }}>ARCHIVED</span></td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Disattivata manualmente. Non viene più servita ai device. Rimane nello storico versioni per audit o rollback.</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>❌</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>❌</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: 10, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#9a3412' }}>
                <strong>🗃️ Cosa succede quando archivi una configurazione?</strong>
                <div style={{ marginTop: 6 }}>
                  Archiviare significa <strong>disattivare</strong> quel profilo: da quel momento non viene piu servito ai kiosk. I device collegati ricalcolano la configurazione effettiva per quel namespace e applicano il <strong>primo profilo PUBLISHED ancora valido nella gerarchia</strong>.
                </div>
                <div style={{ marginTop: 6 }}>
                  Se esiste una configurazione meno specifica, il kiosk fa fallback a quella. Se invece non esiste nessun altro profilo PUBLISHED per quel namespace, il kiosk torna al <strong>default dell'app</strong> o al comportamento bundled locale.
                </div>
                <div style={{ marginTop: 6 }}>
                  In pratica: archiviare non "spegne" il namespace in assoluto, ma rimuove quel profilo dalla catena di risoluzione. Lo storico resta disponibile per audit e rollback, ma il profilo archiviato non partecipa piu alla precedence finche non viene eventualmente ripubblicato tramite rollback.
                </div>
              </div>
            </div>

            {/* AGGIORNAMENTO REAL-TIME */}
            <div>
              <strong style={{ fontSize: 13.5 }}>⚡ Come i device ricevono gli aggiornamenti</strong>
              <p style={{ margin: '6px 0 4px' }}>
                Il sistema usa <strong>due canali complementari</strong>: <strong>WebSocket</strong> per la propagazione immediata quando previsto, e <strong>heartbeat</strong> come riallineamento garantito. Dove non esiste push immediata, o dove la push è stata volutamente disattivata, il kiosk si riallinea comunque <strong>entro un massimo di 2 minuti</strong> tramite heartbeat, oppure al prossimo startup/bootstrap.
              </p>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Canale</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Quando si attiva</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Latenza</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Funzionamento</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>🔌 WebSocket (push)</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Solo nei flussi che prevedono propagazione immediata</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>&lt; 1 secondo</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>
                      Il backend invia un evento <code>device:config_updated</code> ai device online interessati. Il kiosk invalida la cache locale, riesegue la risoluzione dello scope e scarica la nuova versione effettiva del namespace.
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>💓 Heartbeat (pull)</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Sempre, ogni 2 minuti</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Max 2 minuti</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>
                      Il kiosk invia un heartbeat al backend che risponde con la mappa <code>{`{ namespace → configVersion }`}</code> delle config attive. Se una versione differisce da quella in cache, il device scarica la nuova configurazione. Questo recupera aggiornamenti persi per assenza di rete, device offline o push non prevista.
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ marginTop: 12, background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Quando viene inviata una push WebSocket immediata</div>
                <ul style={{ margin: '0 0 0 18px', padding: 0 }}>
                  <li>Quando pubblichi una nuova config.</li>
                  <li>Quando modifichi e salvi una config gia <strong>PUBLISHED</strong>, se la notifica WebSocket non viene disattivata.</li>
                  <li>Quando fai rollback di una config pubblicata, se la notifica WebSocket non viene disattivata.</li>
                  <li>Quando archivi una config pubblicata, se la notifica WebSocket non viene disattivata.</li>
                  <li>Quando associ una categoria a un locale dal form del locale, se la notifica WebSocket e attiva.</li>
                  <li>Quando disassoci una categoria da un locale dal form del locale, se la notifica WebSocket e attiva.</li>
                  <li>
                    Quando cambi <strong>scope</strong> o <strong>target</strong> di una config gia <strong>PUBLISHED</strong>, se la notifica WebSocket e attiva.
                    <div style={{ marginTop: 8, color: '#475569', lineHeight: 1.7 }}>
                      In questo caso i device coinvolti si dividono in tre gruppi:
                    </div>
                    <div style={{ marginTop: 6, color: '#475569', lineHeight: 1.7 }}>
                      <strong>Entranti</strong>: devono ricevere la nuova config perche da adesso rientrano nel target.
                    </div>
                    <div style={{ marginTop: 4, color: '#475569', lineHeight: 1.7 }}>
                      <strong>Uscenti</strong>: devono ricevere la notifica per smettere di usare quella config e rieseguire il fallback corretto.
                    </div>
                    <div style={{ marginTop: 4, color: '#475569', lineHeight: 1.7 }}>
                      <strong>Rimanenti</strong>: devono ricevere la notifica se il contenuto pubblicato e cambiato, perche restano nel target ma la versione effettiva e stata aggiornata.
                    </div>
                    <div style={{ marginTop: 8, color: '#475569', lineHeight: 1.7 }}>
                      In pratica, quando cambi <strong>scope</strong> o <strong>scope entity</strong>, il backend notifica l'<strong>unione tra target precedente e target successivo</strong>, non solo il target finale. Se la checkbox WebSocket e disattivata, anche in questo caso nessuna push immediata viene inviata e il riallineamento avviene al bootstrap o al prossimo heartbeat.
                    </div>
                  </li>
                </ul>
              </div>

              <div style={{ marginTop: 12, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontWeight: 700, color: '#7c2d12', marginBottom: 8 }}>Quando NON viene inviata una push WebSocket immediata</div>
                <ul style={{ margin: '0 0 0 18px', padding: 0, color: '#7c2d12' }}>
                  <li>Quando salvi una bozza <strong>DRAFT</strong>: i device non devono ricevere nulla.</li>
                  <li>Quando nei modali scegli esplicitamente <strong>solo heartbeat</strong> o disattivi la checkbox WebSocket.</li>
                  <li>Quando un device viene spostato da un locale a un altro: attualmente non e prevista una push immediata per questo caso, perche il flusso atteso e riavvio/bootstrap del kiosk. Se il device restasse acceso, il riallineamento avverrebbe comunque entro 2 minuti via heartbeat.</li>
                  <li>In generale, per qualsiasi cambiamento strutturale che oggi non emette l'evento <code>device:config_updated</code>, il recupero e comunque garantito dal heartbeat o dallo startup sync.</li>
                </ul>
              </div>

              <div style={{ marginTop: 12, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, padding: '8px 12px', fontSize: 11.5, color: '#0369a1' }}>
                <strong>💡 Esempio pratico:</strong>
                <br /><br />
                Ogni configurazione ha una <strong>configVersion</strong>, cioe un identificatore derivato dal contenuto effettivo del payload. Ogni 2 minuti il kiosk chiede al backend: <em>"Qual e la versione attuale del namespace?"</em>.
                <br /><br />
                Se la versione che riceve e diversa da quella salvata localmente, scarica subito la nuova configurazione. Questo e il motivo per cui anche senza push immediata il sistema converge comunque da solo entro il ciclo di heartbeat.
                <br /><br />
                La <strong>configVersion</strong> e un hash SHA-256 troncato a 16 caratteri: cambia solo se cambia davvero il contenuto effettivo. Due salvataggi identici non generano un aggiornamento reale.
              </div>
            </div>

            {/* VERSIONI E SALVATAGGIO */}
            <div>
              <strong style={{ fontSize: 13.5 }}>📝 Versioni e salvataggio</strong>
              <ul style={{ margin: '6px 0 0 16px' }}>
                <li>Una config <strong>DRAFT</strong> si aggiorna in place. La versione non cambia e i device non vengono notificati.</li>
                <li>Una config <strong>PUBLISHED</strong> viene ri-pubblicata al salvataggio: la versione aumenta, viene calcolato un nuovo hash e i device vengono notificati via WebSocket. Il bottone <em>Salva e Pubblica</em> è disabilitato finché non si apporta una modifica effettiva — nessun aggiornamento "a vuoto".</li>
                <li>Ogni pubblicazione crea uno <strong>snapshot immutabile</strong> nella cronologia versioni (icona 📜), utile per audit o rollback immediato a una versione precedente.</li>
                <li>Un profilo <strong>ARCHIVED</strong> esce dalla risoluzione effettiva: i kiosk smettono di usarlo e passano alla configurazione PUBLISHED meno specifica ancora disponibile, oppure al default locale se non esiste nessun altro fallback remoto.</li>
              </ul>
              <div style={{ marginTop: 10, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#9a3412' }}>
                <strong>Quando un namespace sparisce dalla risoluzione</strong>
                <div style={{ marginTop: 6, lineHeight: 1.7 }}>
                  Se per un namespace non esiste piu nessun profilo <strong>PUBLISHED</strong> applicabile a quel device, il kiosk considera quella config come <strong>assente</strong>, elimina la versione cached di quel namespace e passa al fallback successivo o al default locale dell'app. Questo vale sia dopo un'archiviazione, sia dopo un cambio di scope/target, sia dopo una rimozione di categoria che esclude il device dalla risoluzione.
                </div>
              </div>
            </div>

            {/* PROTEZIONE ASSET */}
            <div>
              <strong style={{ fontSize: 13.5 }}>🛡️ Protezione asset (immagini)</strong>
              <p style={{ margin: '6px 0 0' }}>
                Un'immagine caricata nella sezione <em>Media Assets</em> non può essere eliminata finché è referenziata in almeno un profilo di configurazione, indipendentemente dallo stato (DRAFT, PUBLISHED o ARCHIVED). Questo previene la rottura di configurazioni attive. Per eliminare un'immagine, rimuoverla prima da tutti i profili che la usano.
              </p>
            </div>

            <div>
              <strong style={{ fontSize: 13.5 }}>📚 Esempi reali di precedenza</strong>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>Esempio 1 — <code>tip_amount</code> a livello Tenant e Venue</div>
                  <div>
                    Supponiamo di avere una config <strong>PUBLISHED</strong> di <code>tip_amount</code> per il tenant <em>"Burger Co."</em> con importi <code>2€ / 5€ / 10€</code>.
                  </div>
                  <div style={{ marginTop: 4 }}>
                    Per il venue <em>"Burger Co. Milano Centrale"</em> pubblichi una config piu specifica con importi <code>1€ / 3€ / 5€</code>.
                  </div>
                  <div style={{ marginTop: 6, color: '#0f172a' }}>
                    <strong>Chi vince?</strong> Vince il profilo <strong>VENUE</strong>, perche e piu specifico di <strong>TENANT</strong>. Quindi i kiosk di quel locale mostreranno <code>1€ / 3€ / 5€</code>, mentre gli altri locali dello stesso tenant continueranno a usare <code>2€ / 5€ / 10€</code>.
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
                    Se archivi la config del venue, i kiosk di quel locale torneranno automaticamente alla config tenant <code>2€ / 5€ / 10€</code>.
                  </div>
                </div>

                <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>Esempio 2 — <code>carousel</code> a livello Global e Tenant</div>
                  <div>
                    Hai un profilo <strong>GLOBAL</strong> con il carousel standard SoonTip, valido per tutti i kiosk che non hanno una config piu specifica.
                  </div>
                  <div style={{ marginTop: 4 }}>
                    Poi pubblichi un profilo <strong>TENANT</strong> per <em>"Hotel Aurora"</em> con immagini personalizzate della struttura.
                  </div>
                  <div style={{ marginTop: 6, color: '#0f172a' }}>
                    <strong>Chi vince?</strong> Per tutti i kiosk del tenant <em>Hotel Aurora</em> vince il profilo <strong>TENANT</strong>, quindi vedranno il carousel brandizzato hotel. Tutti gli altri tenant continueranno a vedere il carousel <strong>GLOBAL</strong>.
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
                    Se archivi il carousel tenant e non esiste una config venue o device ancora piu specifica, quei kiosk torneranno al carousel globale SoonTip. Se invece non esistesse nemmeno il globale, il kiosk userebbe le immagini statiche bundled nell'app.
                  </div>
                </div>

                <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>Esempio 3 — modifica dello scope di una config <code>tip_text</code></div>
                  <div>
                    Supponiamo di avere una config <strong>PUBLISHED</strong> di <code>tip_text</code> con scope <strong>VENUE</strong> per il locale <em>Ristorante Roma Centro</em>, con titolo <code>"Grazie del tuo supporto"</code>.
                  </div>
                  <div style={{ marginTop: 4 }}>
                    Successivamente modifichi quella stessa config e cambi il target: non vale piu solo per quel locale, ma diventa una config <strong>TENANT</strong> valida per tutti i locali del tenant.
                  </div>
                  <div style={{ marginTop: 6, color: '#0f172a' }}>
                    <strong>Cosa succede lato device?</strong> I kiosk del locale originario restano nel target, ma devono comunque ricevere la notifica se il contenuto pubblicato e cambiato. I kiosk degli altri locali del tenant <strong>entrano</strong> nel target e iniziano a ricevere quel testo. Se invece il cambio di scope avesse escluso alcuni device prima coperti, quei device <strong>uscenti</strong> riceverebbero comunque la notifica per smettere di usare quella config.
                  </div>
                  <div style={{ marginTop: 6, color: '#0f172a' }}>
                    <strong>Come funziona il fallback nel kiosk?</strong> Quando un device esce dal target, il kiosk invalida la config di quel namespace e riesegue la risoluzione della gerarchia: prima cerca una config <strong>DEVICE</strong>, poi <strong>VENUE</strong>, poi <strong>VENUE_CATEGORY</strong>, poi <strong>TENANT</strong>, poi <strong>GLOBAL</strong>. Se non trova nessun profilo PUBLISHED applicabile, torna al comportamento o al testo di default dell'app.
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
                    Se la checkbox WebSocket e attiva, questo riallineamento avviene subito per i device online. Se la checkbox e disattiva, oppure un kiosk e offline, l'aggiornamento verra comunque recepito al prossimo heartbeat o al prossimo bootstrap dell'app.
                  </div>
                </div>

                <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>Esempio 4 — associazione o rimozione di una categoria venue</div>
                  <div>
                    Supponiamo che il locale <em>Hotel Aurora Lounge</em> inizialmente non abbia nessuna categoria associata. Esiste pero una config <strong>PUBLISHED</strong> di <code>carousel</code> con scope <strong>VENUE_CATEGORY</strong> per la categoria <em>Hotel</em>.
                  </div>
                  <div style={{ marginTop: 4 }}>
                    Quando dal form del locale associ la categoria <strong>Hotel</strong>, i kiosk di quel locale iniziano a ereditare anche le config pubblicate per <strong>VENUE_CATEGORY = Hotel</strong>, salvo che esista una config piu specifica a livello <strong>DEVICE</strong> o <strong>VENUE</strong>.
                  </div>
                  <div style={{ marginTop: 6, color: '#0f172a' }}>
                    <strong>Cosa vede il kiosk?</strong> Se non aveva una config piu specifica per quel namespace, dopo l'associazione ricalcola la gerarchia e trova la config della categoria. Se invece quella categoria viene poi <strong>rimossa</strong>, il kiosk smette di usare la config <strong>VENUE_CATEGORY</strong> e passa automaticamente al prossimo fallback disponibile: prima <strong>TENANT</strong>, poi <strong>GLOBAL</strong>, oppure al default locale dell'app se non esiste nessun profilo remoto applicabile.
                  </div>
                  <div style={{ marginTop: 6, color: '#0f172a' }}>
                    <strong>Perche questo esempio e utile?</strong> Perche chiarisce che l'associazione categoria non "sovrascrive per forza" quello che vede il device: entra semplicemente nella catena di risoluzione. Quindi il kiosk non applica in modo cieco la config della categoria, ma ricalcola sempre l'intera precedenza e prende il profilo piu specifico disponibile in quel momento.
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
                    Se nel modale lasci attiva la checkbox WebSocket, i device online del locale si riallineano subito. Se la disattivi, oppure se un device e offline, la nuova situazione viene comunque recepita al prossimo heartbeat o al prossimo bootstrap.
                  </div>
                </div>
              </div>
            </div>

            <div>
              <strong style={{ fontSize: 13.5 }}>🧭 Glossario rapido</strong>
              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                  <strong>Namespace</strong>: il tipo di configurazione che stai gestendo, ad esempio <code>carousel</code>, <code>tip_amount</code>, <code>tip_text</code> o <code>review_qr</code>.
                </div>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                  <strong>Scope</strong>: il livello a cui si applica la config, ad esempio <strong>DEVICE</strong>, <strong>VENUE</strong>, <strong>VENUE_CATEGORY</strong>, <strong>TENANT</strong> o <strong>GLOBAL</strong>.
                </div>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                  <strong>Target</strong>: l'entita concreta colpita dalla config dentro quello scope. Esempio: se lo scope e <strong>VENUE</strong>, il target e il singolo locale selezionato; se lo scope e <strong>TENANT</strong>, il target e quel tenant; se lo scope e <strong>GLOBAL</strong>, il target coincide con tutti i device.
                </div>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                  <strong>configVersion</strong>: l'identificatore della versione effettiva della config. Cambia quando cambia davvero il contenuto e serve al kiosk per capire se deve aggiornarsi.
                </div>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                  <strong>Startup sync</strong>: controllo iniziale fatto all'avvio del kiosk per riallineare subito le config prima dell'uso normale dell'app.
                </div>
              </div>
            </div>

          </div>
        </details>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-input" value={filterScope} onChange={e => setFilterScope(e.target.value)} style={{ maxWidth: 160 }}>
            <option value="">Tutti gli scope</option>
            {SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="form-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ maxWidth: 140 }}>
            <option value="">Tutti gli stati</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            className="form-input"
            value={filterNamespace}
            onChange={e => setFilterNamespace(e.target.value)}
            style={{ maxWidth: 210 }}
          >
            <option value="">Tutti i namespace</option>
            {NAMESPACE_CATALOG.map(n => <option key={n.id} value={n.id}>{n.id}</option>)}
          </select>
          <input
            className="form-input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Cerca namespace, scope, target, hash..."
            style={{ minWidth: 280, flex: '1 1 320px' }}
          />
          <button className="btn btn-primary" onClick={openCreate}>+ Nuova Config</button>
        </div>
      </div>

      {error && <div className="card" style={{ color: 'red' }}>❌ {error}</div>}

      {formOpen && (
        <div className="card" style={{ padding: 0, overflow: 'visible', border: '1px solid #dbe4f0' }}>
          <div style={{
            padding: '18px 20px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 52%, #eff6ff 100%)',
            borderBottom: '1px solid #dbe4f0',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ maxWidth: 760 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 999, background: '#ffffffcc', border: '1px solid #dbe4f0', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 10 }}>
                  <span>{editingId ? '✏️' : '✨'}</span>
                  <span>{formModeBadge}</span>
                </div>
                <h3 style={{ margin: 0, fontSize: 24, color: '#0f172a' }}>{formModeTitle}</h3>
                <p style={{ margin: '8px 0 0', color: '#475569', fontSize: 14, lineHeight: 1.6, maxWidth: 760 }}>
                  {formModeSubtitle}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', minWidth: 260, flex: '1 1 320px' }}>
                <SummaryTile label="Modalità" value={editingId ? 'Editing' : 'Creazione'} tone={editingId ? 'blue' : 'emerald'} />
                <SummaryTile label="Namespace" value={form.namespace || 'Da selezionare'} tone={form.namespace ? 'amber' : 'slate'} />
                <SummaryTile label="Scope" value={form.scope || 'Da selezionare'} tone="slate" />
                <SummaryTile label="Target" value={effectiveTargetLabel} tone={form.scopeEntityId || form.scope === 'GLOBAL' ? 'blue' : 'slate'} />
              </div>
            </div>
          </div>

          <div style={{ padding: '18px 20px 20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 920 }}>
              <FormSection
                eyebrow="Contesto"
                title="Dove si applica"
                subtitle="Definisci lo scope della configurazione e, se necessario, il target specifico a cui deve applicarsi."
                background="#fcfdff"
                border="#dbe4f0"
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Scope *</div>
                    <select className="form-input" value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value, scopeEntityId: '' }))}>
                      {SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  {form.scope !== 'GLOBAL' && (
                    <label style={{ display: 'block' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>{SCOPE_ENTITY_LABEL[form.scope]} *</div>
                      {form.scope === 'TENANT' ? (
                        <SearchableSelect
                          options={tenantsForSelect.map(t => ({ value: t.id, label: t.company_name || t.id }))}
                          value={form.scopeEntityId}
                          onChange={v => setForm(f => ({ ...f, scopeEntityId: v }))}
                          placeholder="— Seleziona Tenant —"
                        />
                      ) : scopeOptionsLoading ? (
                        <select className="form-input" disabled><option>Caricamento...</option></select>
                      ) : (
                        <SearchableSelect
                          options={scopeOptions}
                          value={form.scopeEntityId}
                          onChange={v => setForm(f => ({ ...f, scopeEntityId: v }))}
                          placeholder="— Seleziona —"
                        />
                      )}
                    </label>
                  )}
                </div>
                <div style={{ marginTop: 12, padding: '10px 12px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, fontSize: 12, color: '#64748b' }}>
                  La configurazione verrà risolta dal kiosk secondo la priorità degli scope: un profilo più specifico sovrascrive quelli più generali dello stesso namespace.
                </div>
              </FormSection>

              <FormSection
                eyebrow="Contenuto"
                title="Cosa viene inviato al kiosk"
                subtitle="Scegli il namespace e compila il payload con l'editor visuale o, se serve, in JSON raw."
                background="#fffdfa"
                border="#f1e4cf"
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Namespace *</div>
                    <select
                      className="form-input"
                      value={selectedNamespaceMeta ? form.namespace : (form.namespace ? '__custom__' : '')}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === '__custom__' || v === '') { setForm(f => ({ ...f, namespace: v === '__custom__' ? '' : '' })); return; }
                        const cat = NAMESPACE_CATALOG.find(n => n.id === v);
                        const namespaceChanged = v !== form.namespace;
                        const isEmpty = !form.payloadText || form.payloadText.trim() === '{}' || form.payloadText.trim() === '';
                        setShowRawPayload(false);
                        setForm(f => ({
                          ...f,
                          namespace: v,
                          ...(cat && (isEmpty || namespaceChanged) ? { payloadText: JSON.stringify(cat.defaultPayload, null, 2) } : {}),
                        }));
                      }}
                    >
                      <option value="">— Seleziona namespace —</option>
                      {NAMESPACE_CATALOG.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
                      <option value="__custom__">✏️ Personalizzato…</option>
                    </select>
                    {!selectedNamespaceMeta && (
                      <input
                        className="form-input"
                        value={form.namespace}
                        onChange={e => setForm(f => ({ ...f, namespace: e.target.value }))}
                        placeholder="namespace-personalizzato"
                        style={{ marginTop: 8 }}
                      />
                    )}
                    {selectedNamespaceMeta && (
                      <span style={{ fontSize: 12, color: '#6b7280', marginTop: 6, display: 'block', lineHeight: 1.5 }}>
                        {selectedNamespaceMeta.description}
                      </span>
                    )}
                  </label>

                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                      <div>
                        <strong style={{ fontSize: 14, color: '#111827' }}>Payload *</strong>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                          {SMART_NAMESPACES.includes(form.namespace)
                            ? 'Per i namespace supportati puoi alternare editor visuale e JSON senza perdere il controllo del payload.'
                            : 'Per i namespace personalizzati usa direttamente il payload JSON.'}
                        </div>
                      </div>
                      {SMART_NAMESPACES.includes(form.namespace) && (
                        <button type="button" className="btn" style={{ fontSize: 11, padding: '4px 10px' }}
                          onClick={() => setShowRawPayload(v => !v)}>
                          {showRawPayload ? '🎛️ Usa editor visuale' : '{ } Modifica JSON'}
                        </button>
                      )}
                    </div>
              {(showRawPayload || !SMART_NAMESPACES.includes(form.namespace)) ? (
                <textarea
                  className="form-input"
                  value={form.payloadText}
                  onChange={e => { setForm(f => ({ ...f, payloadText: e.target.value })); setPayloadError(null); }}
                  rows={10}
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                />
              ) : (() => {
                // ── carousel smart editor ─────────────────────────────────
                if (form.namespace === 'carousel') {
                  let parsed = {};
                  try { parsed = JSON.parse(form.payloadText); } catch(e) {}
                  const items = Array.isArray(parsed.items) ? parsed.items : [];
                  const updateItems = (newItems) =>
                    setForm(f => ({ ...f, payloadText: JSON.stringify({ ...parsed, items: newItems }, null, 2) }));
                  // Derive carousel type from items that already have a mimeType assigned
                  const assignedMimes = items.filter(it => it.mimeType);
                  const carouselMediaType = assignedMimes.length === 0
                    ? null
                    : assignedMimes.every(it => it.mimeType.startsWith('image/'))
                      ? 'image'
                      : assignedMimes.every(it => it.mimeType.startsWith('video/'))
                        ? 'video'
                        : 'mixed';
                  // Assets filtered by type only (no folder filter yet — used for folder counts)
                  const typeFilteredAssets = mediaAssets.filter(a => {
                    if (!a.mimeType?.startsWith('image/') && !a.mimeType?.startsWith('video/')) return false;
                    if (carouselMediaType === 'image') return a.mimeType.startsWith('image/');
                    if (carouselMediaType === 'video') return a.mimeType.startsWith('video/');
                    return true;
                  });
                  // filter assets by type + selected folder
                  const visibleAssets = typeFilteredAssets.filter(a => {
                    if (!carouselFolderFilter) return true;
                    if (carouselFolderFilter === '__none__') return !a.folder;
                    return a.folder === carouselFolderFilter || a.folder?.startsWith(carouselFolderFilter + '/');
                  });
                  return (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', background: '#fafafa' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, color: '#555' }}>Media del carousel ({items.length})</span>
                          {carouselMediaType === 'image' && <span style={{ fontSize: 11, background: '#dbeafe', color: '#1e40af', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>🖼️ Solo immagini</span>}
                          {carouselMediaType === 'video' && <span style={{ fontSize: 11, background: '#fce7f3', color: '#9d174d', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>🎬 Solo video</span>}
                          {carouselMediaType === 'mixed' && <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>⚠️ Tipo misto</span>}
                        </div>
                        <button type="button" className="btn" onClick={() =>
                          updateItems([...items, { assetId: '', title: '', order: items.length + 1 }])}
                        >+ Aggiungi media</button>
                      </div>
                      {/* Mixed type warning */}
                      {carouselMediaType === 'mixed' && (
                        <div style={{ marginBottom: 8, padding: '7px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 5, fontSize: 12, color: '#92400e' }}>
                          ⚠️ Questa configurazione contiene immagini <em>e</em> video mescolati. Il kiosk utilizzerà il flusso <strong>solo immagini</strong> per questo carousel. Rimuovi o sostituisci gli elementi per avere un tipo uniforme.
                        </div>
                      )}
                      {/* Folder filter */}
                      {mediaFolders.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0 }}>📁 Filtra cartella:</span>
                          <select
                            className="form-input"
                            style={{ fontSize: 12, padding: '4px 8px', flex: 1 }}
                            value={carouselFolderFilter}
                            onChange={e => setCarouselFolderFilter(e.target.value)}
                          >
                            <option value="">Tutte le cartelle ({typeFilteredAssets.length} media)</option>
                            {mediaFolders.map(f => {
                              const count = typeFilteredAssets.filter(a => a.folder === f || a.folder?.startsWith(f + '/')).length;
                              return <option key={f} value={f}>📁 {f} ({count})</option>;
                            })}
                            <option value="__none__">— Senza cartella ({typeFilteredAssets.filter(a => !a.folder).length})</option>
                          </select>
                          {carouselFolderFilter && (
                            <button type="button" className="btn" style={{ fontSize: 11, padding: '3px 8px', flexShrink: 0 }}
                              onClick={() => setCarouselFolderFilter('')}>✕ Rimuovi filtro</button>
                          )}
                        </div>
                      )}
                      {mediaAssetsLoading && <p style={{ fontSize: 13, color: '#888' }}>Caricamento assets…</p>}
                      {!mediaAssetsLoading && items.length === 0 && (
                        <p style={{ fontSize: 13, color: '#aaa' }}>Nessun media configurato. Clicca "+ Aggiungi media".</p>
                      )}
                      {!mediaAssetsLoading && items.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                          background: '#fff', border: '1px solid #e5e7eb', padding: '6px 8px', borderRadius: 5 }}>
                          {/* Reorder buttons */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                            <button type="button" className="btn icon-btn" title="Sposta su"
                              disabled={idx === 0}
                              style={{ fontSize: 7, padding: '1px 4px', lineHeight: '10px', minWidth: 0, height: 14, opacity: idx === 0 ? 0.25 : 1 }}
                              onClick={() => { const a = [...items]; [a[idx-1], a[idx]] = [a[idx], a[idx-1]]; updateItems(a); }}>▲</button>
                            <button type="button" className="btn icon-btn" title="Sposta giù"
                              disabled={idx === items.length - 1}
                              style={{ fontSize: 7, padding: '1px 4px', lineHeight: '10px', minWidth: 0, height: 14, opacity: idx === items.length - 1 ? 0.25 : 1 }}
                              onClick={() => { const a = [...items]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; updateItems(a); }}>▼</button>
                          </div>
                          <span style={{ fontSize: 12, color: '#aaa', minWidth: 18 }}>{idx+1}.</span>
                          {item.assetId && (
                            item.mimeType?.startsWith('video/')
                              ? <div style={{ width: 48, height: 36, background: '#0f172a', borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🎬</div>
                              : <img src={apiService.getMediaPreviewUrl(item.assetId)} alt="" style={{ width: 48, height: 36, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
                          )}
                          <select className="form-input" style={{ flex: 1 }} value={item.assetId || ''}
                            onChange={e => {
                              const selectedAsset = mediaAssets.find(a => a.id === e.target.value);
                              updateItems(items.map((it, i) => i === idx ? { ...it, assetId: e.target.value, mimeType: selectedAsset?.mimeType } : it));
                            }}>
                            <option value="">— Seleziona media —</option>
                            {(() => {
                              // group by folder using optgroup
                              const grouped = {};
                              visibleAssets.forEach(a => {
                                const key = a.folder || '__none__';
                                if (!grouped[key]) grouped[key] = [];
                                grouped[key].push(a);
                              });
                              const keys = Object.keys(grouped).sort((a, b) => {
                                if (a === '__none__') return 1;
                                if (b === '__none__') return -1;
                                return a.localeCompare(b);
                              });
                              return keys.map(key => {
                                const label = key === '__none__' ? '— Senza cartella' : `📁 ${key}`;
                                return (
                                  <optgroup key={key} label={label}>
                                    {grouped[key].map(a => (
                                      <option key={a.id} value={a.id}>{a.fileName || a.id.slice(0, 8)}</option>
                                    ))}
                                  </optgroup>
                                );
                              });
                            })()}
                          </select>
                          <input className="form-input" placeholder="Didascalia (opz.)" value={item.title || ''}
                            style={{ width: 160 }}
                            onChange={e => updateItems(items.map((it, i) => i === idx ? { ...it, title: e.target.value } : it))} />
                          <button type="button" className="btn icon-btn" title="Rimuovi" style={{ color: '#dc2626' }}
                            onClick={() => updateItems(items.filter((_, i) => i !== idx))}>✕</button>
                        </div>
                      ))}
                    </div>
                  );
                }
                // ── tip_amount smart editor ─────────────────────────────────
                if (form.namespace === 'tip_amount') {
                  let parsed = {};
                  try { parsed = JSON.parse(form.payloadText); } catch(e) {}
                  const items = Array.isArray(parsed.items) ? parsed.items : [{ amountCents: 200 }, { amountCents: 500 }, { amountCents: 1000 }];
                  const updateAmount = (idx, euros) => {
                    const amountCents = Math.round(parseFloat(euros) * 100);
                    if (isNaN(amountCents) || amountCents <= 0) return;
                    const newItems = items.map((it, i) => i === idx ? { ...it, amountCents } : it);
                    setForm(f => ({ ...f, payloadText: JSON.stringify({ ...parsed, items: newItems }, null, 2) }));
                  };
                  return (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', background: '#fafafa' }}>
                      <div style={{ fontSize: 13, color: '#555', marginBottom: 10, fontWeight: 600 }}>
                        Importi predefiniti (3 pulsanti)
                      </div>
                      {[0, 1, 2].map(idx => {
                        const amountCents = items[idx]?.amountCents ?? 0;
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            <span style={{ fontSize: 13, color: '#888', minWidth: 70 }}>Pulsante {idx + 1}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 14, color: '#374151' }}>€</span>
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                className="form-input"
                                style={{ width: 100 }}
                                value={amountCents ? (amountCents / 100).toFixed(2) : ''}
                                onChange={e => updateAmount(idx, e.target.value)}
                              />
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ marginTop: 10, padding: '6px 10px', background: '#f3f4f6', borderRadius: 5, fontSize: 12, color: '#6b7280' }}>
                        🔒 Il pulsante "Importo libero" è sempre presente e non configurabile.
                      </div>
                    </div>
                  );
                }
                if (form.namespace === 'tip_text') {
                  let parsed = {};
                  try { parsed = JSON.parse(form.payloadText); } catch(e) {}
                  const titleIt = readLocalizedText(parsed.title, 'it');
                  const titleEn = readLocalizedText(parsed.title, 'en');
                  const subtitleIt = readLocalizedText(parsed.subtitle, 'it');
                  const subtitleEn = readLocalizedText(parsed.subtitle, 'en');
                  const smallTitleFontSize = typeof parsed.small?.titleFontSize === 'number'
                    ? String(parsed.small.titleFontSize)
                    : typeof parsed.titleFontSize === 'number'
                      ? String(parsed.titleFontSize)
                      : '';
                  const smallSubtitleFontSize = typeof parsed.small?.subtitleFontSize === 'number'
                    ? String(parsed.small.subtitleFontSize)
                    : typeof parsed.subtitleFontSize === 'number'
                      ? String(parsed.subtitleFontSize)
                      : '';
                  const mediumLargeTitleFontSize = typeof parsed.mediumLarge?.titleFontSize === 'number'
                    ? String(parsed.mediumLarge.titleFontSize)
                    : typeof parsed.titleFontSize === 'number'
                      ? String(parsed.titleFontSize)
                      : '';
                  const mediumLargeSubtitleFontSize = typeof parsed.mediumLarge?.subtitleFontSize === 'number'
                    ? String(parsed.mediumLarge.subtitleFontSize)
                    : typeof parsed.subtitleFontSize === 'number'
                      ? String(parsed.subtitleFontSize)
                      : '';
                  const updateLocalizedField = (field, lang, value) => {
                    setForm(f => ({
                      ...f,
                      payloadText: JSON.stringify({
                        ...parsed,
                        [field]: {
                          ...(isPlainObject(parsed[field]) ? parsed[field] : {}),
                          [lang]: value,
                        },
                      }, null, 2),
                    }));
                  };
                  const updateBreakpointOptionalNumberField = (breakpointKey, field, value) => {
                    const next = { ...parsed };
                    const currentBreakpoint = (next[breakpointKey] && typeof next[breakpointKey] === 'object') ? next[breakpointKey] : {};
                    if (value === '') {
                      const updatedBreakpoint = { ...currentBreakpoint };
                      delete updatedBreakpoint[field];
                      if (Object.keys(updatedBreakpoint).length === 0) {
                        delete next[breakpointKey];
                      } else {
                        next[breakpointKey] = updatedBreakpoint;
                      }
                    } else {
                      const parsedValue = parseInt(value, 10);
                      if (!isNaN(parsedValue) && parsedValue > 0) {
                        next[breakpointKey] = {
                          ...currentBreakpoint,
                          [field]: parsedValue,
                        };
                      }
                    }
                    delete next.titleFontSize;
                    delete next.subtitleFontSize;
                    setForm(f => ({
                      ...f,
                      payloadText: JSON.stringify(next, null, 2),
                    }));
                  };
                  return (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', background: '#fafafa' }}>
                      <div style={{ fontSize: 13, color: '#555', marginBottom: 10, fontWeight: 600 }}>
                        Testi schermata mancia
                      </div>
                      <div style={{ marginBottom: 10, padding: '10px 12px', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6, fontSize: 12, color: '#9a3412', lineHeight: 1.5 }}>
                        Questo namespace richiede testi bilingue. Compila sempre entrambe le varianti: EN e IT.
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', background: '#fff' }}>
                          <div style={{ fontSize: 13, color: '#374151', fontWeight: 700, marginBottom: 8 }}>Titolo</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>English (EN)</div>
                          <input
                            type="text"
                            className="form-input"
                            value={titleEn}
                            placeholder={TIP_TEXT_DEFAULT_TITLE.en}
                            maxLength={80}
                            onChange={e => updateLocalizedField('title', 'en', e.target.value)}
                          />
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 4 }}>Italiano (IT)</div>
                          <input
                            type="text"
                            className="form-input"
                            value={titleIt}
                            placeholder={TIP_TEXT_DEFAULT_TITLE.it}
                            maxLength={80}
                            onChange={e => updateLocalizedField('title', 'it', e.target.value)}
                          />
                        </div>
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', background: '#fff' }}>
                          <div style={{ fontSize: 13, color: '#374151', fontWeight: 700, marginBottom: 8 }}>Sottotitolo</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>English (EN)</div>
                          <textarea
                            className="form-input"
                            rows={3}
                            value={subtitleEn}
                            placeholder={TIP_TEXT_DEFAULT_SUBTITLE.en}
                            maxLength={180}
                            onChange={e => updateLocalizedField('subtitle', 'en', e.target.value)}
                          />
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 4 }}>Italiano (IT)</div>
                          <textarea
                            className="form-input"
                            rows={3}
                            value={subtitleIt}
                            placeholder={TIP_TEXT_DEFAULT_SUBTITLE.it}
                            maxLength={180}
                            onChange={e => updateLocalizedField('subtitle', 'it', e.target.value)}
                          />
                        </div>
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', background: '#fff' }}>
                          <div style={{ fontSize: 12, color: '#374151', marginBottom: 8, fontWeight: 600 }}>Small tablet</div>
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Dimensione titolo</div>
                              <input
                                type="number"
                                min="12"
                                max="96"
                                step="1"
                                className="form-input"
                                style={{ width: 130 }}
                                value={smallTitleFontSize}
                                placeholder="Default app"
                                onChange={e => updateBreakpointOptionalNumberField('small', 'titleFontSize', e.target.value)}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Dimensione sottotitolo</div>
                              <input
                                type="number"
                                min="10"
                                max="72"
                                step="1"
                                className="form-input"
                                style={{ width: 160 }}
                                value={smallSubtitleFontSize}
                                placeholder="Default app"
                                onChange={e => updateBreakpointOptionalNumberField('small', 'subtitleFontSize', e.target.value)}
                              />
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
                            Default di riferimento app: titolo 48, sottotitolo 24.
                          </div>
                        </div>
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', background: '#fff' }}>
                          <div style={{ fontSize: 12, color: '#374151', marginBottom: 8, fontWeight: 600 }}>Medium / Large tablet</div>
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Dimensione titolo</div>
                              <input
                                type="number"
                                min="12"
                                max="96"
                                step="1"
                                className="form-input"
                                style={{ width: 130 }}
                                value={mediumLargeTitleFontSize}
                                placeholder="Default app"
                                onChange={e => updateBreakpointOptionalNumberField('mediumLarge', 'titleFontSize', e.target.value)}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Dimensione sottotitolo</div>
                              <input
                                type="number"
                                min="10"
                                max="72"
                                step="1"
                                className="form-input"
                                style={{ width: 160 }}
                                value={mediumLargeSubtitleFontSize}
                                placeholder="Default app"
                                onChange={e => updateBreakpointOptionalNumberField('mediumLarge', 'subtitleFontSize', e.target.value)}
                              />
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
                            Default di riferimento app: titolo 56, sottotitolo 21.
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                          Lascia vuoto per usare i default dell'app per ciascuna categoria di tablet.
                        </div>
                      </div>
                    </div>
                  );
                }
                if (form.namespace === 'review_qr') {
                  let parsed = {};
                  try { parsed = JSON.parse(form.payloadText); } catch(e) {}

                  const timeoutSeconds = Number.isFinite(parsed.timeoutSeconds)
                    ? String(parsed.timeoutSeconds)
                    : String(REVIEW_QR_TIMEOUT_DEFAULT_SECONDS);
                  const reviewUrl = typeof parsed.reviewUrl === 'string' ? parsed.reviewUrl : '';
                  const showHintText = typeof parsed.showHintText === 'boolean' ? parsed.showHintText : false;
                  const hintTextIt = readLocalizedText(parsed.hintText, 'it');
                  const hintTextEn = readLocalizedText(parsed.hintText, 'en');
                  const subtitleTextIt = readLocalizedText(parsed.subtitleText, 'it');
                  const subtitleTextEn = readLocalizedText(parsed.subtitleText, 'en');

                  const updateReviewPayload = (patch) => {
                    const { enabled, ...rest } = parsed;
                    const next = { ...rest, ...patch };
                    setForm(f => ({
                      ...f,
                      payloadText: JSON.stringify(next, null, 2),
                    }));
                  };

                  const updateReviewLocalizedField = (field, lang, value) => {
                    const source = isPlainObject(parsed[field]) ? parsed[field] : {};
                    updateReviewPayload({
                      [field]: {
                        ...source,
                        [lang]: value,
                      },
                    });
                  };

                  const urlError = reviewUrl.trim().length > 0 && !isValidAbsoluteReviewUrl(reviewUrl)
                    ? 'Inserisci una URL assoluta valida (http/https).'
                    : null;

                  return (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', background: '#fafafa' }}>
                      <div style={{ fontSize: 13, color: '#555', marginBottom: 10, fontWeight: 600 }}>
                        QR recensioni in schermata pagamento riuscito
                      </div>

                      <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6, fontSize: 12, color: '#9a3412', lineHeight: 1.5 }}>
                        Questo namespace richiede testi bilingue. Compila sempre entrambe le varianti: EN e IT.
                      </div>

                      <div style={{ marginBottom: 12, fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
                        Nota: cancellando o archiviando la regola si ritorna allo stato di default, senza QR code delle recensioni.
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) minmax(220px, 2fr)', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Timeout rientro (secondi)</div>
                          <input
                            type="number"
                            min={REVIEW_QR_TIMEOUT_MIN_SECONDS}
                            max={REVIEW_QR_TIMEOUT_MAX_SECONDS}
                            step="1"
                            className="form-input"
                            value={timeoutSeconds}
                            onChange={(e) => {
                              const next = Number(e.target.value);
                              if (!Number.isFinite(next)) {
                                updateReviewPayload({ timeoutSeconds: REVIEW_QR_TIMEOUT_DEFAULT_SECONDS });
                                return;
                              }
                              const bounded = Math.min(Math.max(Math.round(next), REVIEW_QR_TIMEOUT_MIN_SECONDS), REVIEW_QR_TIMEOUT_MAX_SECONDS);
                              updateReviewPayload({ timeoutSeconds: bounded });
                            }}
                          />
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                            Default: {REVIEW_QR_TIMEOUT_DEFAULT_SECONDS}s. Range: {REVIEW_QR_TIMEOUT_MIN_SECONDS}-{REVIEW_QR_TIMEOUT_MAX_SECONDS}s.
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>URL recensione</div>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="https://example.com/review"
                            value={reviewUrl}
                            onChange={(e) => updateReviewPayload({ reviewUrl: e.target.value })}
                          />
                          {urlError && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 6 }}>{urlError}</div>}
                          {!urlError && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                              Accetta link assoluti HTTP o HTTPS.
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 4 }}>Testo sopra al QR (EN)</div>
                        <input type="text" className="form-input" placeholder={REVIEW_QR_DEFAULT_SUBTITLE_TEXT.en} value={subtitleTextEn}
                          onChange={(e) => updateReviewLocalizedField('subtitleText', 'en', e.target.value)} />
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 4 }}>Testo sopra al QR (IT)</div>
                        <input type="text" className="form-input" placeholder={REVIEW_QR_DEFAULT_SUBTITLE_TEXT.it} value={subtitleTextIt}
                          onChange={(e) => updateReviewLocalizedField('subtitleText', 'it', e.target.value)} />
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                          Default app quando il QR è attivo: IT "{REVIEW_QR_DEFAULT_SUBTITLE_TEXT.it}" / EN "{REVIEW_QR_DEFAULT_SUBTITLE_TEXT.en}".
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, marginTop: 12 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Testo sotto al QR (opzionale)</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: showHintText ? '#0f766e' : '#94a3b8', minWidth: 60, textAlign: 'right' }}>
                            {showHintText ? 'Mostra' : 'Nascondi'}
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={showHintText}
                            aria-label="Mostra o nascondi testo sotto al QR"
                            onClick={() => updateReviewPayload({ showHintText: !showHintText })}
                            style={{
                              width: 42,
                              height: 24,
                              borderRadius: 999,
                              border: `1px solid ${showHintText ? '#0f766e' : '#cbd5e1'}`,
                              background: showHintText ? '#0f766e' : '#e2e8f0',
                              padding: 2,
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease, border-color 0.2s ease',
                            }}
                          >
                            <span
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                background: '#ffffff',
                                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.25)',
                                transform: showHintText ? 'translateX(18px)' : 'translateX(0)',
                                transition: 'transform 0.2s ease',
                              }}
                            />
                          </button>
                        </div>
                      </div>

                      <div style={{ marginTop: 12, opacity: showHintText ? 1 : 0.55 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 4 }}>Testo sotto al QR (EN)</div>
                        <input type="text" className="form-input" placeholder={REVIEW_QR_DEFAULT_HINT_TEXT.en} value={hintTextEn}
                          onChange={(e) => updateReviewLocalizedField('hintText', 'en', e.target.value)} disabled={!showHintText}
                          maxLength={REVIEW_QR_HINT_TEXT_MAX_LENGTH} />
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 4 }}>Testo sotto al QR (IT)</div>
                        <input type="text" className="form-input" placeholder={REVIEW_QR_DEFAULT_HINT_TEXT.it} value={hintTextIt}
                          onChange={(e) => updateReviewLocalizedField('hintText', 'it', e.target.value)} disabled={!showHintText}
                          maxLength={REVIEW_QR_HINT_TEXT_MAX_LENGTH} />
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                          Default app: IT "{REVIEW_QR_DEFAULT_HINT_TEXT.it}" / EN "{REVIEW_QR_DEFAULT_HINT_TEXT.en}". Disattivato di default. Max {REVIEW_QR_HINT_TEXT_MAX_LENGTH} caratteri per lingua.
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              {payloadError && <span style={{ color: 'red', fontSize: 12, marginTop: 3, display: 'block' }}>{payloadError}</span>}
                  </div>
                </div>
              </FormSection>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                  {isEditingPublished
                    ? 'Questo profilo è pubblicato: al salvataggio verrà eseguita una nuova pubblicazione solo se il contenuto è cambiato.'
                    : 'Puoi salvare questa configurazione come bozza e pubblicarla in un secondo momento dalla tabella profili.'}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" onClick={save}
                    disabled={isSaveDisabled}
                    title={disabledTitle}>
                    {saving ? 'Salvataggio...' : (isEditingPublished ? '💾 Salva e Pubblica' : 'Salva Bozza')}
                  </button>
                  <button className="btn" onClick={() => setFormOpen(false)}>Annulla</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {versionsProfileId && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>📜 Cronologia Versioni</h3>
            <button className="btn" onClick={() => setVersionsProfileId(null)}>✕ Chiudi</button>
          </div>
          {versionsLoading ? <p>Caricamento...</p> : versions.length === 0 ? <p>Nessuna versione presente.</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 4px', borderBottom: '1px solid #eee' }}>Versione</th>
                  <th style={{ textAlign: 'left', padding: '6px 4px', borderBottom: '1px solid #eee' }}>Hash</th>
                  <th style={{ textAlign: 'left', padding: '6px 4px', borderBottom: '1px solid #eee' }}>Pubblicato il</th>
                  <th style={{ textAlign: 'left', padding: '6px 4px', borderBottom: '1px solid #eee' }}>Da</th>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #eee' }}></th>
                </tr>
              </thead>
              <tbody>
                {versions.map(v => (
                  <React.Fragment key={v.id}>
                    <tr>
                      <td style={{ padding: '6px 4px' }}>v{v.version}</td>
                      <td style={{ padding: '6px 4px', fontFamily: 'monospace', fontSize: 11, color: '#888' }}>{v.configVersion}</td>
                      <td style={{ padding: '6px 4px', fontSize: 11 }}>{new Date(v.publishedAt).toLocaleString('it-IT')}</td>
                      <td style={{ padding: '6px 4px', fontSize: 11 }}>{v.publishedBy || '—'}</td>
                      <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
                          <button className="btn icon-btn" title="Dettagli versione" onClick={() => openVersionView(v)}>
                            <VisibilityIcon fontSize="small" />
                          </button>
                          <button className="btn" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', height: 32 }} onClick={() => openRollbackModal(versionsProfileId, v.id)}>
                            Ripristina
                          </button>
                        </div>
                      </td>
                    </tr>
                    {viewingVersion?.id === v.id && (
                      <tr>
                        <td colSpan={5} style={{ padding: '0 4px 12px 4px' }}>
                          <div style={{
                            marginTop: 6,
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            padding: '14px 16px',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                              <div style={{ fontWeight: 700, color: '#334155' }}>🕘 Dettaglio Versione</div>
                              <button className="btn" style={{ fontSize: 11 }} onClick={() => setViewingVersion(null)}>Chiudi</button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 13, marginBottom: 12 }}>
                              <div><strong>Scope:</strong> {viewingVersion.scope}</div>
                              {viewingVersion.scope !== 'GLOBAL' && (viewingVersion.scopeEntityLabel || viewingVersion.scopeEntityId) && (
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <strong>Entità Scope:</strong> {viewingVersion.scopeEntityLabel || viewingVersion.scopeEntityId}
                                </div>
                              )}
                              <div><strong>Tipo:</strong> <span style={{ color: '#6b7280', fontWeight: 600 }}>SNAPSHOT STORICO</span></div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <strong>Profile ID:</strong>{' '}
                                <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#475569' }}>{viewingVersion.profileId || versionsProfileId || '—'}</span>
                              </div>
                              <div><strong>Versione:</strong> v{viewingVersion.version}</div>
                              <div><strong>Hash:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{viewingVersion.configVersion || '—'}</span></div>
                              {viewingVersion.publishedAt && (
                                <div style={{ gridColumn: '1 / -1' }}><strong>Pubblicato il:</strong> {new Date(viewingVersion.publishedAt).toLocaleString('it-IT')}</div>
                              )}
                            </div>
                            <ConfigPayloadDetails profile={viewingVersion} mediaAssets={mediaAssets} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="card">
        {loading ? (
          <p>Caricamento...</p>
        ) : visibleProfiles.length === 0 ? (
          <p>Nessun profilo di configurazione trovato.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}><button type="button" onClick={() => handleSort('namespace')} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 700, color: '#111827' }}>Namespace{sortIndicator('namespace')}</button></th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}><button type="button" onClick={() => handleSort('scope')} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 700, color: '#111827' }}>Scope{sortIndicator('scope')}</button></th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}><button type="button" onClick={() => handleSort('scopeEntity')} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 700, color: '#111827' }}>Entità Scope{sortIndicator('scopeEntity')}</button></th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}><button type="button" onClick={() => handleSort('status')} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 700, color: '#111827' }}>Stato{sortIndicator('status')}</button></th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}><button type="button" onClick={() => handleSort('version')} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 700, color: '#111827' }}>Versione{sortIndicator('version')}</button></th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}><button type="button" onClick={() => handleSort('configVersion')} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 700, color: '#111827' }}>Hash{sortIndicator('configVersion')}</button></th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}><button type="button" onClick={() => handleSort('publishedAt')} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 700, color: '#111827' }}>Pubblicato il{sortIndicator('publishedAt')}</button></th>
                <th style={{ textAlign: 'left', padding: '8px 4px', borderBottom: '1px solid #eee' }}></th>
              </tr>
            </thead>
            <tbody>
              {visibleProfiles.map(p => (
                <React.Fragment key={p.id}>
                  <tr>
                    <td style={{ padding: '8px 4px', fontWeight: 600 }}>{p.namespace}</td>
                    <td style={{ padding: '8px 4px', fontSize: 12, color: '#555' }}>{p.scope}</td>
                    <td style={{ padding: '8px 4px', fontSize: 12, color: '#555' }}>{p.scopeEntityLabel || (p.scopeEntityId ? <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#aaa' }}>{p.scopeEntityId.slice(0, 8)}…</span> : '—')}</td>
                    <td style={{ padding: '8px 4px' }}>
                      <span style={{ color: statusColor[p.status] || '#333', fontWeight: 600 }}>{p.status}</span>
                    </td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>v{p.version}</td>
                    <td style={{ padding: '8px 4px', fontSize: 11, color: '#aaa', fontFamily: 'monospace' }}>{p.configVersion || '—'}</td>
                    <td style={{ padding: '8px 4px', fontSize: 11 }}>{p.publishedAt ? new Date(p.publishedAt).toLocaleString('it-IT') : '—'}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn icon-btn" title="Visualizza payload" onClick={() => openView(p.id)}><VisibilityIcon fontSize="small" /></button>
                      {p.status !== 'ARCHIVED' && (
                        <button className="btn icon-btn" title="Modifica" onClick={() => openEdit(p.id)}><EditIcon fontSize="small" /></button>
                      )}
                      {p.status === 'DRAFT' && (
                        <button className="btn btn-primary icon-btn" title="Pubblica" onClick={() => publish(p.id)}><PublishIcon fontSize="small" /></button>
                      )}
                      {p.status === 'PUBLISHED' && (
                        <button className="btn icon-btn" title="Archivia (disattiva)" onClick={() => archive(p.id)}><ArchiveIcon fontSize="small" /></button>
                      )}
                      <button className="btn icon-btn" title="Storico versioni" onClick={() => openVersions(p.id)}><HistoryIcon fontSize="small" /></button>
                      {p.status !== 'PUBLISHED' && (
                        <button className="btn btn-danger icon-btn" title="Elimina" onClick={() => remove(p.id)}><DeleteIcon fontSize="small" sx={{ color: 'white' }} /></button>
                      )}
                    </td>
                  </tr>
                  {viewingProfile?.id === p.id && viewingProfile.__viewMode === 'profile' && (
                    <tr>
                      <td colSpan={8} style={{ padding: '0 4px 12px 4px' }}>
                        <div style={{
                          marginTop: 6,
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          padding: '14px 16px',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <div style={{ fontWeight: 700, color: '#334155' }}>🔍 Dettaglio Config</div>
                            <button className="btn" style={{ fontSize: 11 }} onClick={() => setViewingProfile(null)}>Chiudi</button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 13, marginBottom: 12 }}>
                            <div><strong>Scope:</strong> {viewingProfile.scope}</div>
                            {viewingProfile.scope !== 'GLOBAL' && (viewingProfile.scopeEntityLabel || viewingProfile.scopeEntityId) && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <strong>Entità Scope:</strong> {viewingProfile.scopeEntityLabel || viewingProfile.scopeEntityId}
                              </div>
                            )}
                            <div><strong>Stato:</strong> <span style={{ color: statusColor[viewingProfile.status] || '#333', fontWeight: 600 }}>{viewingProfile.status}</span></div>
                            <div style={{ gridColumn: '1 / -1' }}>
                              <strong>Profile ID:</strong>{' '}
                              <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#475569' }}>{viewingProfile.id || '—'}</span>
                            </div>
                            <div><strong>Versione:</strong> v{viewingProfile.version}</div>
                            <div><strong>Hash:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{viewingProfile.configVersion || '—'}</span></div>
                            {viewingProfile.publishedAt && (
                              <div style={{ gridColumn: '1 / -1' }}><strong>Pubblicato il:</strong> {new Date(viewingProfile.publishedAt).toLocaleString('it-IT')}</div>
                            )}
                          </div>
                          <ConfigPayloadDetails profile={viewingProfile} mediaAssets={mediaAssets} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Publish/Archive/Rollback Confirmation Modal ── */}
      {pubModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 16px',
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640,
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ background: '#1e3a5f', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{pubModal.mode === 'archive' ? '🗃️' : pubModal.mode === 'rollback' ? '⏪' : '📡'}</span>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
                  {pubModal.mode === 'save'
                    ? 'Salva e Pubblica'
                    : pubModal.mode === 'archive'
                      ? 'Archivia Configurazione'
                      : pubModal.mode === 'rollback'
                        ? 'Ripristina Versione'
                        : 'Pubblica Configurazione'}
                </span>
              </div>
              <button onClick={() => setPubModal(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Profile info */}
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: 5, padding: '3px 10px', fontSize: 13, fontWeight: 600 }}>
                  {pubModal.namespace}
                </span>
                <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 5, padding: '3px 10px', fontSize: 13 }}>
                  {pubModal.scope}
                </span>
              </div>

              {/* Impacted devices box */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>
                  Dispositivi impattati
                </div>
                <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 10, lineHeight: 1.5 }}>
                  {pubModal.mode === 'archive'
                    ? 'I device interessati ricalcoleranno la configurazione effettiva. Se esiste una config pubblicata a scope inferiore o superiore secondo la precedence, useranno quella; altrimenti faranno fallback ai valori statici dell’app.'
                    : pubModal.mode === 'rollback'
                      ? 'I device interessati riceveranno di nuovo questa versione storica come configurazione effettiva del namespace selezionato.'
                    : 'I device interessati riceveranno questa configurazione come nuova config effettiva per il namespace selezionato.'}
                </div>
                {pubModalLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888', fontSize: 14 }}>
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                    Calcolo in corso…
                  </div>
                ) : pubModalDevices ? (
                  <div>
                    {showImpactedDiff && (
                      <div style={{
                        marginBottom: 12,
                        background: '#fff7ed',
                        border: '1px solid #fdba74',
                        borderRadius: 8,
                        padding: '10px 12px',
                        fontSize: 12,
                        color: '#9a3412',
                        lineHeight: 1.55,
                      }}>
                        {impactedDiffMessage}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: pubModalDevices.devices?.length > 0 ? 8 : 0 }}>
                      <span style={{ fontSize: 22 }}>🖥️</span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: pubModalDevices.count === 0 ? '#9ca3af' : '#1e3a5f' }}>
                        {pubModalDevices.count}
                      </span>
                      <span style={{ fontSize: 14, color: '#374151' }}>
                        {pubModalDevices.count === 0
                          ? 'nessun device attivo — la config sarà disponibile al prossimo dispositivo che si connette'
                          : pubModalDevices.count === 1 ? 'device coinvolto' : 'device coinvolti'}
                      </span>
                    </div>
                    {showImpactedDiff ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 8 }}>
                        <div style={{ background: '#ffffff', border: '1px solid #dbeafe', borderRadius: 8, padding: '10px 12px' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginBottom: 6 }}>Target finale ({pubModalDevices.nextCount})</div>
                          <DeviceBadgeList devices={pubModalDevices.nextDevices} tone="blue" emptyLabel="Il nuovo target non include device attivi." />
                        </div>
                        <div style={{ background: '#ffffff', border: '1px solid #dcfce7', borderRadius: 8, padding: '10px 12px' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 6 }}>Entrano nel target ({pubModalDevices.addedCount})</div>
                          <DeviceBadgeList devices={pubModalDevices.addedDevices} tone="green" emptyLabel="Nessun nuovo device aggiunto." />
                        </div>
                        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>Restano nel target ({pubModalDevices.retainedCount})</div>
                          <DeviceBadgeList devices={pubModalDevices.retainedDevices} tone="slate" emptyLabel="Nessun device resta sullo stesso target." />
                        </div>
                        <div style={{ background: '#ffffff', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 12px' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#be123c', marginBottom: 6 }}>Escono dal target ({pubModalDevices.removedCount})</div>
                          <DeviceBadgeList devices={pubModalDevices.removedDevices} tone="rose" emptyLabel="Nessun ex device da riallineare." />
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        <DeviceBadgeList devices={pubModalDevices.devices} tone="blue" emptyLabel="Nessun device attivo associato a questo target." />
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* WS notify toggle */}
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px',
                  background: pubWsNotify ? '#eff6ff' : '#fff',
                  cursor: 'pointer', transition: 'background 0.15s',
                  borderBottom: '1px solid #e2e8f0',
                }}>
                  <input
                    type="checkbox"
                    checked={pubWsNotify}
                    onChange={e => setPubWsNotify(e.target.checked)}
                    style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1e3a5f' }}>
                      ⚡ Notifica istantanea via WebSocket
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                      {pubModal.mode === 'archive'
                        ? 'I device connessi ricalcolano subito la config effettiva dopo l’archiviazione.'
                        : pubModal.mode === 'rollback'
                          ? 'I device connessi ripristinano subito questa versione storica.'
                        : 'I device connessi ricevono la nuova configurazione entro ~50ms dalla pubblicazione.'}
                    </div>
                  </div>
                </label>
                <div style={{ padding: '10px 16px', background: '#f8fafc', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>💓</span>
                  <div style={{ fontSize: 12, color: '#4b5563' }}>
                    {pubWsNotify
                      ? pubModal.mode === 'archive'
                        ? <><strong>WebSocket attivo.</strong> I device online ricalcolano subito la config; i device offline o non connessi si allineeranno comunque al prossimo heartbeat o al prossimo startup.</>
                        : pubModal.mode === 'rollback'
                          ? <><strong>WebSocket attivo.</strong> I device online ricevono subito la versione ripristinata; i device offline o non connessi si allineeranno comunque al prossimo heartbeat o al prossimo startup.</>
                        : <><strong>WebSocket attivo.</strong> I device offline o non connessi si aggiorneranno comunque al prossimo heartbeat (30s – 1min).</>
                      : pubModal.mode === 'archive'
                        ? <><strong>Solo heartbeat.</strong> Nessuna notifica push inviata. I device vedranno la config archiviata come assente al prossimo heartbeat o al prossimo startup, e poi applicheranno l’eventuale fallback remoto o statico.</>
                        : pubModal.mode === 'rollback'
                          ? <><strong>Solo heartbeat.</strong> Nessuna notifica push inviata. I device riceveranno la versione ripristinata al prossimo heartbeat o al prossimo startup.</>
                        : <><strong>Solo heartbeat.</strong> Nessuna notifica push inviata. I device riceveranno la nuova config al prossimo heartbeat. Utile per ridurre l'overhead o pianificare aggiornamenti graduali.</>
                    }
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button className="btn" style={{ minWidth: 90 }} onClick={() => setPubModal(null)}>
                  Annulla
                </button>
                <button
                  className="btn btn-primary"
                  style={{ minWidth: 180, fontWeight: 600 }}
                  onClick={handlePubConfirm}
                  disabled={pubModalLoading}
                >
                  {pubModal.mode === 'save'
                    ? '💾 Conferma Salva e Pubblica'
                    : pubModal.mode === 'archive'
                      ? '🗃️ Conferma Archiviazione'
                      : pubModal.mode === 'rollback'
                        ? '⏪ Conferma Ripristino'
                      : '✅ Conferma Pubblicazione'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {errorModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100,
          background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 16px',
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
            boxShadow: '0 24px 70px rgba(15,23,42,0.28)',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #7f1d1d 0%, #9f1239 100%)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{errorModal.icon}</span>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{errorModal.title}</span>
              </div>
              <button
                onClick={() => setErrorModal(null)}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.75)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{
                background: errorModal.bgColor,
                border: `1px solid ${errorModal.borderColor}`,
                borderRadius: 10,
                padding: '14px 16px',
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: errorModal.accentColor, marginBottom: 8 }}>
                  Dettaglio
                </div>
                <div style={{ color: '#334155', lineHeight: 1.6, fontSize: 14, whiteSpace: 'pre-wrap' }}>
                  {errorModal.message}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" style={{ minWidth: 96 }} onClick={() => setErrorModal(null)}>
                  Ho capito
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemoteDeviceConfig;
